import { API } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import {
  BulkChangeKind,
  BulkChanges,
  BulkLocationChanges,
  BulkUnmatchedRow,
  DocumentIngestLocationOption,
  DocumentUploadInterface,
  DuplicateLocationConflict,
  FacilityInfoFieldType,
  FacilityInfoSelectOption,
  GroupInterface,
  LocationMatchOption,
  PolicyNeedsReviewGroup,
  PolicyReviewGroup
} from "@swivl-mono/types";
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  Modal,
  ModalSize,
  Popover,
  PopoverMenu,
  PopoverPosition,
  SearchSelect,
  SelectItem,
  Spinner,
  Tooltip
} from "@swivl-mono/ui";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiAlertCircle, FiArrowLeft, FiCheck, FiMoreVertical, FiRefreshCw, FiRotateCcw, FiX } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";

import LocationsModel from "../../../Models/Locations/Locations.Model";
import { Member } from "../../../Models/Member/Member.Current";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import { buildLocationCheckboxOptions, buildLocationPickerOptions } from "./LocationOptions";
import { NeedsReviewList } from "./PolicyNeedsReviewList";
import { PolicyReviewHandlers, PolicyUpdatesList } from "./PolicyUpdatesList";
import { PolicyWalkThrough } from "./PolicyWalkThrough";

/**
 * Guided review for a bulk/document upload: a content-dependent step flow —
 * Location details → Policies → Apply → Done. Details AND policies are both STAGED
 * (assigns/edits/exclusions persist to the interpretation, nothing is written live)
 * until the final Apply commits them in one bulk pass. Steps are built only from
 * what the document actually contains, and detail columns are generated only for
 * fields that were found.
 */

const KIND_ORDER: Record<BulkChangeKind, number> = { info: 0, schedule: 1, amenity: 2, service: 3, storage_type: 4, policy: 5 };
const isFeatureKind = (kind: BulkChangeKind): boolean => kind === "amenity" || kind === "service" || kind === "storage_type";

// Rendered value used for presence/diff detection (feature cells display a toggle, not this text):
// a feature is "found" when it has a yes/no; info/schedule show the text. Empty → dash.
const renderValue = (kind: BulkChangeKind, value: string | null): string => {
  if (isFeatureKind(kind)) return value === "1" ? "✓" : value === "0" ? "✗" : "—";
  return value === null || value === "" ? "—" : value;
};

// Feature toggle value → human label for the "was → now" tooltip.
const yesNoLabel = (value: string | null | undefined): string => (value === "1" ? "Yes" : value === "0" ? "No" : "—");

// A select field's stored value (e.g. "rv-boat") → its catalog label (e.g. "RV / Boat") for display.
function selectValueLabel(column: Column, value: string | null | undefined): string {
  if (!value) return "—";
  return column.selectOptions?.find((option) => option.value === value)?.label ?? value;
}

// HTML input type/inputMode for a facility-info field's typed value — phone/email/url get their
// semantic keyboard + basic browser validation; select fields never reach here (own <select> below).
function inputPropsFor(fieldType: FacilityInfoFieldType | undefined): { type: string; inputMode?: "tel" | "email" | "url" } {
  if (fieldType === "phone") return { type: "tel", inputMode: "tel" };
  if (fieldType === "email") return { type: "email", inputMode: "email" };
  if (fieldType === "url") return { type: "url", inputMode: "url" };
  return { type: "text" };
}

interface FieldSelectProps {
  column: Column;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  className: string;
}

// Shared select control for a facility-info field with known options (e.g. Persona) — a select
// field only ever accepts one of its own options, never arbitrary typed text (the actual fix for
// "edit state let 'yes' become '85' with no error thrown" — this replaces the raw text input).
function FieldSelect({ column, value, disabled, onChange, className }: FieldSelectProps): React.ReactElement {
  return (
    <select value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} className={className}>
      <option value="">Not set</option>
      {(column.selectOptions ?? []).map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

interface Column {
  mapKey: string;
  label: string;
  kind: BulkChangeKind;
  /** Facility-info fields only — drives the edit control (select dropdown vs. typed input). */
  fieldType?: FacilityInfoFieldType;
  selectOptions?: FacilityInfoSelectOption[];
}

interface GridCell {
  value: string | null;
  changed: boolean;
  previous: string | null;
  /** Set once this field has actually been written (approve-as-you-go or the final Apply). */
  appliedAt?: string;
  /** Operator dismissed this field (e.g. a lease's signing-entity info wrongly read as the
   * facility's own) — never written, but stays visible (grayed, with an undo) rather than vanish. */
  excluded?: boolean;
}

interface GridRow {
  key: string;
  locationName: string | null; // null = unmatched row (needs a location picked)
  locationId: number | null; // set for matched rows — the edit target for inline staging
  rowIndex: number | null; // set for unmatched rows (used to assign)
  readName: string | null; // the location name we READ from the document (unmatched rows) — a suggestion
  cells: Map<string, GridCell>;
  /** Unmatched rows only — policy updates this row also carries, invisible until a location is assigned. */
  policyCount: number;
  /** Unmatched rows only — best-first candidate locations the matcher wasn't confident enough to
   * auto-assign (a near-miss fuzzy match, or an LLM-suggested semantic match) — never auto-applied,
   * but the operator can one-click accept the top one via "Confirm location" instead of searching. */
  alternatives: LocationMatchOption[];
  /** Address context for disambiguating same-named locations (N2): the location's own for a
   * matched row, the one read from the document for an unmatched row. */
  city?: string;
  state?: string;
  /** Matched rows only — fields this location's duplicate rows still disagree on (any varSystem). */
  conflicts: DuplicateLocationConflict[];
  /** True once this location had something approved AND nothing is left pending — distinct from a
   * location that simply never had any changes to begin with (which never gets an appliedAt at all).
   * Drives the sort-to-bottom + orange "Reviewed" state; always false for unmatched rows. */
  reviewed: boolean;
}

// Cortex mapKeys that carry the location's own address — when one of these is itself a proposed
// change, the static city/state subline would just repeat what the change row already says (N3).
const ADDRESS_MAPKEYS = new Set(["address", "city", "state", "postal_code"]);

function hasAddressChange(cells: Map<string, GridCell>): boolean {
  return [...ADDRESS_MAPKEYS].some((key) => cells.get(key)?.changed);
}

// A location has something left to approve when at least one changed field hasn't been written yet
// and hasn't been dismissed either; once every changed field carries appliedAt or excluded, there's
// nothing pending — Approve has nothing left to do.
function hasUnapprovedChange(cells: Map<string, GridCell>): boolean {
  return [...cells.values()].some((cell) => cell.changed && !cell.appliedAt && !cell.excluded);
}

// Flatten matched locations + unmatched rows into a single (dynamic) column set + row list.
function buildGrid(changes: BulkChanges): { columns: Column[]; rows: GridRow[] } {
  const columnByKey = new Map<string, Column>();
  const addColumn = (
    mapKey: string,
    label: string,
    kind: BulkChangeKind,
    fieldType?: FacilityInfoFieldType,
    selectOptions?: FacilityInfoSelectOption[]
  ): void => {
    if (!columnByKey.has(mapKey)) columnByKey.set(mapKey, { mapKey, label, kind, fieldType, selectOptions });
  };

  const rows: GridRow[] = [];

  for (const location of changes.locations) {
    const cells = new Map<string, GridCell>();
    for (const change of location.data) {
      addColumn(change.mapKey, change.label, change.kind, change.fieldType, change.selectOptions);
      cells.set(change.mapKey, {
        value: change.proposedValue,
        changed: change.changed,
        previous: change.currentValue,
        ...(change.appliedAt ? { appliedAt: change.appliedAt } : {}),
        ...(change.excluded ? { excluded: change.excluded } : {})
      });
    }
    if (cells.size > 0)
      rows.push({
        key: `loc-${location.locationId}`,
        locationName: location.locationName,
        locationId: location.locationId,
        rowIndex: location.rowIndex, // lets a matched row be re-pointed to a different location
        readName: null,
        cells,
        policyCount: 0,
        alternatives: [],
        conflicts: changes.conflicts.filter((conflict) => conflict.locationId === location.locationId),
        ...(location.city ? { city: location.city } : {}),
        ...(location.state ? { state: location.state } : {}),
        // Something was actually approved OR dismissed AND nothing's left pending — a location with
        // zero proposed changes never gets an appliedAt/excluded at all, so this never fires for
        // "never had anything," only for "had things, now handled" (written or deliberately skipped).
        reviewed: [...cells.values()].some((cell) => cell.appliedAt || cell.excluded) && !hasUnapprovedChange(cells)
      });
  }

  for (const row of changes.unmatched) {
    if (row.dismissed) continue; // shown in the separate "Dismissed" section instead
    const cells = new Map<string, GridCell>();
    for (const found of row.found) {
      addColumn(found.mapKey, found.label, found.kind, found.fieldType, found.selectOptions);
      cells.set(found.mapKey, { value: found.value, changed: false, previous: null });
    }
    // Every non-dismissed row here already needs a location assigned — that's the whole reason it's
    // in `unmatched` — regardless of whether it also carries extra found data. Gating on cells/policy
    // content used to hide a row with neither (e.g. one the roster scan identified by name alone,
    // with nothing else extracted for it), which silently broke the 1:1 match between this list and
    // the "N locations couldn't be matched" count above it — always render, matching that count.
    rows.push({
      key: `row-${row.rowIndex}`,
      locationName: null,
      locationId: null,
      rowIndex: row.rowIndex,
      readName: row.identity.name?.trim() || null,
      cells,
      policyCount: row.policyCount,
      alternatives: row.alternatives,
      conflicts: [],
      // The document's address is a match key, never a proposed change (ENG-3306), so it shows here as read-only context instead.
      ...(row.identity.city?.trim() ? { city: row.identity.city.trim() } : {}),
      ...(row.identity.state?.trim() ? { state: row.identity.state.trim() } : {}),
      reviewed: false // unmatched rows need matching, not approving — never "reviewed"
    });
  }

  const columns = [...columnByKey.values()].sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.label.localeCompare(b.label));
  // Stable sort (Array.prototype.sort is stable) — reviewed locations sink to the bottom, everything
  // else keeps its original relative order. Recomputed fresh from `changes` every load, so editing a
  // reviewed location's field again (un-reviewing it) naturally moves it back up on the next refresh.
  const sortedRows = [...rows].sort((a, b) => Number(a.reviewed) - Number(b.reviewed));
  return { columns, rows: sortedRows };
}

interface ConflictResolutionListProps {
  conflicts: DuplicateLocationConflict[];
  resolvingKeys: Set<string>;
  onResolve: (locationId: number, mapKey: string, keepValue: string) => void;
}

// A conflict whose every competing value is the bare string "0"/"1" is a boolean field (a feature
// toggle, or a policy yes/no) — shown as "Yes"/"No", not the raw stored value. A real text/select
// conflict's proposedValue is never going to literally be "0" or "1", so this is a safe, local
// check rather than threading field-kind context into this shared component.
function isBooleanConflict(options: { proposedValue: string }[]): boolean {
  return options.every((option) => option.proposedValue === "0" || option.proposedValue === "1");
}

// Shared conflict-resolution UI — LocationCard's inline red strip (By-location) and LocationCell's
// popover (Compare-all) both render this; same buttons, same behavior, just a different container.
function ConflictResolutionList({ conflicts, resolvingKeys, onResolve }: ConflictResolutionListProps): React.ReactElement {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-[12px] font-semibold text-red-700">Matched twice: pick which value is right for each field.</p>
      {conflicts.map((conflict) => {
        const key = `${conflict.locationId}::${conflict.mapKey}`;
        const busy = resolvingKeys.has(key);
        // Two duplicate rows can propose the SAME value while a third disagrees — one button per distinct value.
        const distinctOptions = [...new Map(conflict.options.map((option) => [option.proposedValue, option])).values()];
        const boolean = isBooleanConflict(distinctOptions);
        return (
          <div key={key} className="flex flex-col gap-1">
            <span className="text-[12px] text-grey-600">{conflict.label}</span>
            <span className="flex flex-wrap items-center gap-1.5">
              {distinctOptions.map((option) => (
                <button
                  key={option.proposedValue}
                  type="button"
                  disabled={busy}
                  onClick={() => onResolve(conflict.locationId, conflict.mapKey, option.proposedValue)}
                  title={option.citation}
                  className="rounded-[4px] border border-grey-300 bg-white px-2.5 py-1 text-[12px] font-semibold text-grey-700 transition-colors hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700 disabled:opacity-50"
                >
                  {boolean ? (option.proposedValue === "1" ? "Yes" : "No") : `Keep “${option.proposedValue}”`}
                </button>
              ))}
            </span>
          </div>
        );
      })}
    </div>
  );
}

interface LocationCellProps {
  row: GridRow;
  locationOptions: SelectItem[];
  onAssign: (rowIndex: number, value: string) => void;
  resolvingConflictKeys: Set<string>;
  onResolveConflict: (locationId: number, mapKey: string, keepValue: string) => void;
  onApprove: (locationId: number) => Promise<void>;
  approving: boolean;
  /** Drops this matched location from the review entirely — restorable via the "Dropped locations" strip. */
  onDismiss: (rowIndex: number) => void;
  dismissing: boolean;
  /** Dismiss (or restore) a location's detail change(s) — "Dismiss all details" is offered from
   * this cell's row-actions menu. */
  onDismissFinding: (locationId: number, excluded: boolean, mapKey?: string) => void;
}

// The location column for a row: a matched location's name, or — when the document's location didn't
// match one of ours — the name we READ from the document (a suggestion) plus a picker to confirm or
// change which location these details apply to. A name mismatch never hides the row's data.
function LocationCell({
  row,
  locationOptions,
  onAssign,
  resolvingConflictKeys,
  onResolveConflict,
  onApprove,
  approving,
  onDismiss,
  dismissing,
  onDismissFinding
}: LocationCellProps): React.ReactElement {
  const [reassigning, setReassigning] = useState(false);
  const [conflictsOpen, setConflictsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const matched = row.locationName !== null;
  // Best-guess suggestion (a near-miss fuzzy match, or an LLM-suggested semantic match) — never
  // auto-applied, but a one-click way to accept it instead of searching the full location list.
  const topAlternative = !matched ? row.alternatives[0] : undefined;

  // Matched: name (+ a "swirling arrows" button to re-point it) on its own line, city/state
  // underneath — suppressed when the address is itself a proposed change (its own change cell
  // already says it). Approve/row-actions sit in their own cluster to the right.
  const addressSuffix = !hasAddressChange(row.cells) ? [row.city, row.state].filter(Boolean).join(", ") : "";
  if (matched && !reassigning) {
    return (
      <div className="flex min-w-0 items-center justify-between gap-1.5">
        <div className="flex min-w-0 flex-col">
          <div className="flex min-w-0 items-center gap-1.5">
            <span title={row.locationName ?? undefined} className="truncate font-semibold text-grey-900">
              {row.locationName}
            </span>
            {row.conflicts.length > 0 && (
              <Popover
                isOpen={conflictsOpen}
                onOpenChange={setConflictsOpen}
                side="bottom"
                position={PopoverPosition.LEFT}
                className="w-80"
                trigger={
                  <button
                    type="button"
                    aria-label="Resolve conflicting fields"
                    className="flex shrink-0 items-center"
                    onClick={(event) => {
                      event.stopPropagation();
                      setConflictsOpen((value) => !value);
                    }}
                  >
                    <FiAlertCircle className="size-3.5 text-red-500" />
                  </button>
                }
              >
                <ConflictResolutionList
                  conflicts={row.conflicts}
                  resolvingKeys={resolvingConflictKeys}
                  onResolve={(locationId, mapKey, keepValue) => {
                    onResolveConflict(locationId, mapKey, keepValue);
                    setConflictsOpen(false);
                  }}
                />
              </Popover>
            )}
          </div>
          {addressSuffix && (
            <span title={addressSuffix} className="truncate text-[11px] text-grey-500">
              {addressSuffix}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {hasUnapprovedChange(row.cells) && (
            <button
              type="button"
              disabled={approving}
              onClick={() => onApprove(row.locationId as number)}
              aria-label="Approve this location's changes"
              title="Approve"
              className="shrink-0 rounded-full p-1 text-orange-500 transition-colors hover:bg-orange-50 disabled:opacity-50"
            >
              <FiCheck className="size-3.5" />
            </button>
          )}
          {row.rowIndex !== null && (
            <Popover
              isOpen={menuOpen}
              onOpenChange={setMenuOpen}
              disabled={dismissing}
              side="bottom"
              position={PopoverPosition.RIGHT}
              className="w-44"
              trigger={
                <button
                  type="button"
                  aria-label="Row actions"
                  className="flex size-6 items-center justify-center rounded-full text-grey-400 transition-colors hover:bg-grey-100 hover:text-grey-900"
                  onClick={(event) => {
                    event.stopPropagation();
                    setMenuOpen((value) => !value);
                  }}
                >
                  <FiMoreVertical size={16} />
                </button>
              }
            >
              <PopoverMenu
                entries={[
                  {
                    label: "Change the location",
                    icon: <FiRefreshCw size={14} />,
                    onClick: () => {
                      setMenuOpen(false);
                      setReassigning(true);
                    }
                  },
                  ...(hasUnapprovedChange(row.cells)
                    ? [
                        {
                          label: "Dismiss all details",
                          icon: <FiX size={14} />,
                          onClick: () => {
                            setMenuOpen(false);
                            onDismissFinding(row.locationId as number, true);
                          }
                        }
                      ]
                    : []),
                  {
                    label: "Remove the location",
                    danger: true,
                    onClick: () => {
                      setMenuOpen(false);
                      onDismiss(row.rowIndex as number);
                    }
                  }
                ]}
              />
            </Popover>
          )}
        </div>
      </div>
    );
  }

  // Unmatched (or reassigning a matched row) → the location picker.
  return (
    <div className="flex w-full flex-col gap-1.5">
      {matched ? (
        <div className="text-[11px] text-grey-500">
          Reassigning <span className="font-medium text-grey-700">{row.locationName}</span>, pick a location
        </div>
      ) : row.readName ? (
        <div>
          <div className="text-sm font-semibold text-grey-900">{row.readName}</div>
          {addressSuffix && <div className="truncate text-[11px] text-grey-500">{addressSuffix}</div>}
          <div className="text-[11px] text-grey-500">read from the document, confirm the location</div>
          {topAlternative && (
            <button
              type="button"
              onClick={() => onAssign(row.rowIndex as number, `loc:${topAlternative.locationId}`)}
              title={`Suggested: ${topAlternative.name}`}
              className="mt-1 truncate rounded-[4px] border border-teal-200 bg-teal-50 px-2 py-0.5 text-[11px] font-semibold text-teal-700 transition-colors hover:bg-teal-100"
            >
              Confirm location: {topAlternative.name}
            </button>
          )}
        </div>
      ) : (
        <div className="text-[11px] text-grey-500">No location matched, pick one</div>
      )}
      {row.rowIndex !== null && (
        <SearchSelect
          selectedValues={[]}
          options={locationOptions}
          onChange={(values) => {
            if (values[0]) {
              onAssign(row.rowIndex as number, values[0]);
              setReassigning(false);
            }
          }}
          placeholder="Assign a location…"
          hasSearchInDropdown
        />
      )}
    </div>
  );
}

interface ChangedGroup {
  family: string;
  fields: number;
  locations: number | "all";
}

const FAMILY_ORDER = ["Contact details", "Hours", "Amenities", "Policy language"] as const;
function familyOf(kind: BulkChangeKind): string {
  if (kind === "schedule") return "Hours";
  if (kind === "amenity" || kind === "service" || kind === "storage_type") return "Amenities";
  if (kind === "policy") return "Policy language";
  return "Contact details";
}

/**
 * What actually got WRITTEN, for the Done screen: field families (Contact / Hours / Amenities /
 * Policy language) with how many fields applied across how many locations, plus the set of
 * touched locations. Computed from the diff (still in state — not reloaded after finishing), and
 * counts only fields that were actually approved (`appliedAt`/`applied`/`orgApplied`) — approving
 * as you go means Done never force-writes a still-pending field, so a merely-diffed-but-unapproved
 * change must NOT show up here as "what changed".
 */
function computeWhatChanged(changes: BulkChanges): { groups: ChangedGroup[]; locations: { id: number; name: string }[] } {
  const byFamily = new Map<string, { fields: number; locs: Set<number> }>();
  const bump = (family: string, locationId: number, count: number): void => {
    const entry = byFamily.get(family) ?? { fields: 0, locs: new Set<number>() };
    entry.fields += count;
    entry.locs.add(locationId);
    byFamily.set(family, entry);
  };
  const updated = new Map<number, string>();

  for (const location of changes.locations) {
    for (const change of location.data) {
      if (!change.appliedAt) continue;
      bump(familyOf(change.kind), location.locationId, 1);
      updated.set(location.locationId, location.locationName);
    }
  }

  for (const group of changes.policyGroups) {
    if (group.scope === "org") continue; // no locationId to bump against — handled below
    for (const location of group.locations) {
      if (location.excluded || !location.appliedAt) continue;
      bump("Policy language", location.locationId, 1);
      updated.set(location.locationId, location.locationName);
    }
  }

  const groups: ChangedGroup[] = FAMILY_ORDER.filter((family) => family !== "Policy language" && byFamily.has(family)).map((family) => {
    const entry = byFamily.get(family) as { fields: number; locs: Set<number> };
    return { family, fields: entry.fields, locations: entry.locs.size };
  });

  const locationPolicy = byFamily.get("Policy language");
  const orgPolicyFields = changes.policyGroups.filter((group) => group.scope === "org" && !group.orgExcluded && group.orgApplied).length;
  if (locationPolicy) groups.push({ family: "Policy language", fields: locationPolicy.fields, locations: locationPolicy.locs.size });
  else if (orgPolicyFields > 0) groups.push({ family: "Policy language", fields: orgPolicyFields, locations: "all" });

  return { groups, locations: [...updated.entries()].map(([id, name]) => ({ id, name })) };
}

type StepKey = "details" | "policies";
interface FlowStep {
  key: StepKey;
  label: string;
}

export function BulkImportPage(): React.ReactElement {
  const { uploadId } = useParams();
  const navigate = useNavigate();
  const orgId = Member.orgId;
  const id = Number(uploadId);

  const [changes, setChanges] = useState<BulkChanges | null>(null);
  const [upload, setUpload] = useState<DocumentUploadInterface | null>(null);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<GroupInterface[]>([]);
  const [locationAddress, setLocationAddress] = useState<DocumentIngestLocationOption[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  // By-location vs Compare-all details view. null = follow the row-count default; a click pins it for the session.
  const [pickedView, setPickedView] = useState<DetailsView | null>(null);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);

  const back = useCallback((): void => navigate("/settings/document-ingest"), [navigate]);
  const goToPropertyManager = useCallback((): void => navigate("/properties-v3"), [navigate]);
  // Lands back on the same document's Location details, exactly where it left off — not a
  // navigation, since Done is just another step of this same page.
  const keepReviewing = useCallback((): void => {
    setApplied(false);
    setStepIndex(0);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      const [, addressOptions] = await Promise.all([LocationsModel.getGroupsAndLocations(false), API.DocumentIngest.listLocationOptions(orgId)]);
      if (active) {
        setGroups(LocationsModel.state.groups ?? []);
        setLocationAddress(addressOptions);
      }
    })();
    return () => {
      active = false;
    };
  }, [orgId]);

  // Picker options: each group is a greyed, non-selectable header organizing its locations, with an
  // address subline (from the separate DocumentIngest location lookup) disambiguating same-named
  // ones. A row is assigned to ONE location — no "all of group" bulk pick.
  const locationOptions = useMemo<SelectItem[]>(() => {
    const addressById = new Map(locationAddress.map((option) => [option.locationId, option]));
    return buildLocationPickerOptions(groups, addressById);
  }, [groups, locationAddress]);

  // Same grouping, but for the policy step's "Affects N locations" multi-select — every org
  // location (not just the ones this document happened to cover), checkbox-value-shaped.
  const allLocationOptions = useMemo<SelectItem[]>(() => {
    const addressById = new Map(locationAddress.map((option) => [option.locationId, option]));
    return buildLocationCheckboxOptions(groups, addressById);
  }, [groups, locationAddress]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [changesResult, uploadResult] = await Promise.all([
        API.DocumentIngest.getBulkChanges(orgId, id),
        API.DocumentIngest.getUpload(orgId, id)
      ]);
      setChanges(changesResult);
      setUpload(uploadResult);
    } catch (error) {
      AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn't load this document's review, please try again.");
    } finally {
      setLoading(false);
    }
  }, [orgId, id]);

  useEffect(() => {
    if (Number.isInteger(id)) void load();
  }, [id, load]);

  const grid = useMemo(() => (changes ? buildGrid(changes) : { columns: [], rows: [] }), [changes]);

  // Step-rail latch: a step that's ever had content stays in the rail even if approving-as-you-go
  // later empties it out (e.g. approving a policy group's last pending location drops policyGroups
  // to zero) — otherwise the operator gets silently teleported mid-review as steps disappear under
  // them. Only ever grows; nothing removes a key once added.
  const hasDataNow = grid.rows.length > 0;
  const hasPolicyNow =
    !!changes &&
    (changes.policyGroups.length > 0 ||
      changes.policyNeedsReview.length > 0 ||
      changes.unmatched.some((row) => row.policyCount > 0 && !row.dismissed));
  const [seenSteps, setSeenSteps] = useState<Set<string>>(new Set());
  useEffect(() => {
    setSeenSteps((prev) => {
      const next = new Set(prev);
      if (hasDataNow) next.add("details");
      if (hasPolicyNow) next.add("policies");
      return next.size === prev.size ? prev : next;
    });
  }, [hasDataNow, hasPolicyNow]);

  // Approve-as-you-go: ONE global flag disables every Approve button (Details + Policies) while any
  // single approve is in flight — the jsonb bulkMapping read-mutate-write is a whole-document
  // round-trip with no row-level lock, so two concurrent approves would otherwise last-write-wins
  // and silently drop each other's appliedAt stamps after the real writes already landed.
  const [approving, setApproving] = useState(false);

  const handleApproveLocation = useCallback(
    async (locationId: number) => {
      if (approving) return;
      setApproving(true);
      try {
        const result = await API.DocumentIngest.applyBulk(orgId, id, { locationIds: [locationId], scope: "data", finish: false });
        if (result.failedLocations > 0) AlertsModel.errorToast("Couldn’t approve this location, please try again.");
        await load();
      } catch (error) {
        AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t approve this location, please try again.");
      } finally {
        setApproving(false);
      }
    },
    [orgId, id, approving, load]
  );

  // Approve-all confirmation: which scope's modal is open (null = closed). One local modal, not a
  // new shared libs/ui component — generalizing for one consumer is a drive-by refactor.
  const [approveAllScope, setApproveAllScope] = useState<"data" | "policy" | null>(null);

  const handleApproveAllData = useCallback(async () => {
    if (approving) return;
    setApproving(true);
    try {
      // No locationIds/mapKeys filter — the server iterates every matched location itself in one
      // load/save, exactly one call regardless of how many locations/fields that touches.
      const result = await API.DocumentIngest.applyBulk(orgId, id, { scope: "data", finish: false });
      if (result.failedLocations > 0) AlertsModel.errorToast(`${result.failedLocations} location(s) couldn’t be updated.`);
      await load();
    } catch (error) {
      AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t approve everything, please try again.");
    } finally {
      setApproving(false);
      setApproveAllScope(null);
    }
  }, [orgId, id, approving, load]);

  const handleAssign = useCallback(
    async (rowIndex: number, value: string) => {
      let locationIds: number[] = [];
      if (value.startsWith("group:")) {
        const groupId = Number(value.slice("group:".length));
        locationIds = groups.find((group) => group.id === groupId)?.locations.map((location) => location.id) ?? [];
      } else if (value.startsWith("loc:")) {
        locationIds = [Number(value.slice("loc:".length))];
      }
      if (locationIds.length === 0) {
        AlertsModel.errorToast("That group has no locations to assign to.");
        return;
      }
      try {
        const result = await API.DocumentIngest.assignBulkRow(orgId, id, { rowIndex, locationIds });
        if (result) {
          setChanges(result);
          AlertsModel.successToast(`Matched to ${locationIds.length} location${locationIds.length === 1 ? "" : "s"}.`);
        } else {
          AlertsModel.errorToast("Couldn’t match that row, please try again.");
        }
      } catch {
        AlertsModel.errorToast("Couldn’t match that row, please try again.");
      }
    },
    [orgId, id, groups]
  );

  // Stage an inline detail edit (Cortex/schedule) for a matched location — persisted, not written live.
  const handleEditFinding = useCallback(
    async (locationId: number, mapKey: string, value: string) => {
      try {
        const result = await API.DocumentIngest.updateBulkFinding(orgId, id, { locationId, mapKey, value });
        if (result) setChanges(result);
        else AlertsModel.errorToast("Couldn’t save that edit, please try again.");
      } catch (error) {
        AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t save that edit, please try again.");
      }
    },
    [orgId, id]
  );

  // Dismiss (or restore) a location's detail change(s) — one field (mapKey given) or every detail
  // change at that location at once ("Dismiss all details", mapKey omitted) — e.g. a lease's
  // signing-entity info wrongly read as the facility's own. Never touches policy.
  const handleDismissFinding = useCallback(
    async (locationId: number, excluded: boolean, mapKey?: string) => {
      try {
        const result = await API.DocumentIngest.updateBulkFindingExclusion(orgId, id, { locationId, mapKey, excluded });
        if (result) setChanges(result);
        else AlertsModel.errorToast("Couldn’t save that change, please try again.");
      } catch (error) {
        AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t save that change, please try again.");
      }
    },
    [orgId, id]
  );

  // Dismiss (or restore) an unmatched row that isn't one of our locations and never will be —
  // restorable, so it moves into/out of the collapsed "Dismissed" section rather than disappearing.
  const [dismissingRows, setDismissingRows] = useState<Set<number>>(new Set());
  const handleDismissRow = useCallback(
    async (rowIndex: number, dismissed: boolean) => {
      setDismissingRows((prev) => new Set(prev).add(rowIndex));
      try {
        const result = await API.DocumentIngest.dismissUnmatchedRow(orgId, id, { rowIndex, dismissed });
        if (result) setChanges(result);
      } finally {
        setDismissingRows((prev) => {
          const next = new Set(prev);
          next.delete(rowIndex);
          return next;
        });
      }
    },
    [orgId, id]
  );

  // Resolve a duplicate-location conflict: the operator picks which of the disagreeing rows'
  // values is right; every other proposal for that location+field is excluded from applying.
  const [resolvingConflictKeys, setResolvingConflictKeys] = useState<Set<string>>(new Set());
  const handleResolveConflict = useCallback(
    async (locationId: number, mapKey: string, keepValue: string) => {
      const key = `${locationId}::${mapKey}`;
      setResolvingConflictKeys((prev) => new Set(prev).add(key));
      try {
        const result = await API.DocumentIngest.resolveDuplicateConflict(orgId, id, { locationId, mapKey, keepValue });
        if (result) setChanges(result);
      } finally {
        setResolvingConflictKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [orgId, id]
  );

  // Policy review handlers, shared verbatim by the "All updates" list and the Walk Through modal —
  // one place that calls the review-toggle/edit endpoints, so both surfaces stay in lockstep.
  const [busyGroupKeys, setBusyGroupKeys] = useState<Set<string>>(new Set());
  const withBusyGroup = useCallback(async (groupKey: string, task: () => Promise<void>) => {
    setBusyGroupKeys((prev) => new Set(prev).add(groupKey));
    try {
      await task();
    } catch (error) {
      // Shared by every policy-review action (excluded/edit-value/add-locations/approve) — without
      // this, a thrown error left the group's button spinning forever with no feedback at all.
      AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t save that change, please try again.");
    } finally {
      setBusyGroupKeys((prev) => {
        const next = new Set(prev);
        next.delete(groupKey);
        return next;
      });
    }
  }, []);

  const handleToggleExcluded = useCallback(
    (group: PolicyReviewGroup, locationIds: number[] | null, excludedNext: boolean) =>
      void withBusyGroup(group.groupKey, async () => {
        // Keyed by mapKey alone now — grouping no longer keys by value, so there's no single
        // group-wide value left to scope the exclude by (see PolicyReviewGroup.proposedValue).
        const result = await API.DocumentIngest.updatePolicyReview(orgId, id, {
          mapKey: group.mapKey,
          ...(locationIds !== null ? { locationIds } : {}),
          excluded: excludedNext
        });
        if (result) setChanges(result);
      }),
    [orgId, id, withBusyGroup]
  );

  // `locationId` omitted = the group-wide edit (every currently-affected location takes the new
  // value — only reachable from the UI when they already agree on one). Present = a single
  // location's own edit, independent of its siblings — how a "Varies by location" group is
  // corrected via the per-location breakdown. updatePolicyFinding returns the raw upload record
  // (a shared shape with the scrape flow), not the review payload — so this follows up with one
  // refresh, same as a deliberate, infrequent action deserves.
  const handleEditPolicyValue = useCallback(
    (group: PolicyReviewGroup, newValue: string, locationId?: number) =>
      void withBusyGroup(group.groupKey, async () => {
        await API.DocumentIngest.updatePolicyFinding(orgId, id, {
          mapKey: group.mapKey,
          proposedValue: newValue,
          ...(locationId !== undefined
            ? { locationId }
            : group.scope === "locations"
              ? { locationIds: group.locations.map((location) => location.locationId) }
              : {})
        });
        const refreshed = await API.DocumentIngest.getBulkChanges(orgId, id);
        if (refreshed) setChanges(refreshed);
      }),
    [orgId, id, withBusyGroup]
  );

  // Fixes a needs-review policy finding: the operator supplies the real value for every location
  // that hit this exact unrecognized text. Plain updatePolicyFinding — no separate "promote"
  // endpoint — once the server renormalizes it successfully, it drops out of policyNeedsReview and
  // shows up as an ordinary PolicyReviewGroup on the next refresh.
  const handleFixNeedsReview = useCallback(
    (group: PolicyNeedsReviewGroup, newValue: string) =>
      void withBusyGroup(group.groupKey, async () => {
        await API.DocumentIngest.updatePolicyFinding(orgId, id, {
          mapKey: group.mapKey,
          proposedValue: newValue,
          ...(group.scope === "locations" ? { locationIds: group.locations.map((location) => location.locationId) } : {})
        });
        const refreshed = await API.DocumentIngest.getBulkChanges(orgId, id);
        if (refreshed) setChanges(refreshed);
      }),
    [orgId, id, withBusyGroup]
  );

  // Adds a location the scanned document never covered to this policy update — manufactures its
  // finding (and a bare row, if it has none yet) server-side, so it reviews/applies like any other.
  const handleAddPolicyLocations = useCallback(
    (group: PolicyReviewGroup, locationIds: number[]) =>
      void withBusyGroup(group.groupKey, async () => {
        const result = await API.DocumentIngest.addPolicyGroupLocations(orgId, id, {
          mapKey: group.mapKey,
          proposedValue: group.proposedValue,
          locationIds
        });
        if (result) setChanges(result);
      }),
    [orgId, id, withBusyGroup]
  );

  // Approve one policy group (and, when a gated child is folded under it, the child's mapKey in the
  // SAME call — approving the child alone would silently no-op, since the parent's proposed value
  // falls out of scope for gatePasses's lookup). Org-scope groups have no matched rows for applyBulk
  // to iterate, so they go through the legacy per-finding applyFindings path instead (same one
  // Apply already uses for an org-wide-policy-only document).
  const handleApprovePolicyGroup = useCallback(
    async (group: PolicyReviewGroup, childMapKeys: string[] = []) => {
      if (approving) return;
      setApproving(true);
      try {
        const mapKeys = [group.mapKey, ...childMapKeys];
        // No `proposedValue` scoping — grouping is mapKey-only now, so approving writes each
        // matched location's OWN current value (never forcing one shared value onto locations that
        // genuinely differ); exclusion/conflict-blocking already apply per-finding regardless.
        const result =
          group.scope === "org"
            ? await API.DocumentIngest.applyFindings(orgId, id, { mapKeys, finish: false })
            : await API.DocumentIngest.applyBulk(orgId, id, { scope: "policy", mapKeys, finish: false });
        const failed = "failed" in result ? result.failed : result.failedFields;
        if (failed > 0) AlertsModel.errorToast(`${failed} field(s) couldn’t be approved; please review and try again.`);
        const refreshed = await API.DocumentIngest.getBulkChanges(orgId, id);
        if (refreshed) setChanges(refreshed);
      } catch (error) {
        AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t approve this update, please try again.");
      } finally {
        setApproving(false);
      }
    },
    [orgId, id, approving]
  );

  // A document's policyGroups are either ALL "locations"-scope (matched rows) or ALL "org"-scope
  // (org-wide, policy-only — no matched rows for applyBulk to iterate); never mixed. Exactly one
  // server call either way, mirroring the branch handleApply already makes for the same reason.
  const handleApproveAllPolicy = useCallback(async () => {
    if (approving) return;
    setApproving(true);
    try {
      const groups = changes?.policyGroups ?? [];
      const result =
        groups.length > 0 && groups[0].scope === "org"
          ? await API.DocumentIngest.applyFindings(orgId, id, { mapKeys: groups.map((group) => group.mapKey), finish: false })
          : await API.DocumentIngest.applyBulk(orgId, id, { scope: "policy", finish: false });
      const failed = "failed" in result ? result.failed : result.failedFields;
      if (failed > 0) AlertsModel.errorToast(`${failed} field(s) couldn’t be approved; please review and try again.`);
      const refreshed = await API.DocumentIngest.getBulkChanges(orgId, id);
      if (refreshed) setChanges(refreshed);
    } catch (error) {
      AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t approve everything, please try again.");
    } finally {
      setApproving(false);
      setApproveAllScope(null);
    }
  }, [orgId, id, approving, changes]);

  const policyHandlers: PolicyReviewHandlers = {
    onToggleExcluded: handleToggleExcluded,
    onEditValue: handleEditPolicyValue,
    onAddLocations: handleAddPolicyLocations,
    onApprove: handleApprovePolicyGroup,
    busyGroupKeys,
    approving
  };

  // Pure sign-off — approve-as-you-go already wrote everything the operator approved, so Done
  // never force-applies whatever is still pending; it just stamps confirmedAt.
  const handleFinish = useCallback(async () => {
    setApplying(true);
    try {
      await API.DocumentIngest.finishReview(orgId, id);
      setApplied(true);
    } catch (error) {
      // The highest-stakes swallow in this file: without this, the operator has no idea whether
      // this landed, and the button stays stuck "Finishing…" forever.
      AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t finish this review, please try again.");
    } finally {
      setApplying(false);
    }
  }, [orgId, id]);

  // A scan that failed on our side (LLM provider, storage) is retryable in place; the progress shows on
  // the documents list, so go back there once it's re-queued (ENG-3324).
  const handleRetryScan = async (): Promise<void> => {
    try {
      await API.DocumentIngest.retryScan(orgId, id);
      back();
    } catch (error) {
      AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t retry that scan, please try again.");
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // The LLM scan failed on our side. The page can still have rows (a spreadsheet's column read
  // doesn't need the scan), but everything the scan adds, policies included, is missing (ENG-3324).
  const scanFailed = upload?.status === "failed";

  if (!changes) {
    return (
      <div className="mx-auto max-w-2xl px-8 py-16 text-center text-grey-500">
        {scanFailed
          ? "Something went wrong on our end while scanning this file. The file itself is fine, so retrying the scan usually works."
          : "We couldn’t read this file’s locations. It may not be a multi-location spreadsheet, or the file couldn’t be read."}
        <div className="mt-4 flex justify-center gap-2">
          {scanFailed && (
            <Button color={ButtonColor.ORANGE} leftIcon={<FiRefreshCw />} onClick={handleRetryScan}>
              Retry scan
            </Button>
          )}
          <Button variant={ButtonVariant.GHOST} onClick={back}>
            Back to Document Upload
          </Button>
        </div>
      </div>
    );
  }

  const hasData = grid.rows.length > 0;

  // A POLICY-ONLY file with no detail rows — an org-wide policy document, or a policy-only
  // spreadsheet the column reader couldn't turn into rows (it fell back to the LLM scan) — is
  // reviewed once at org scope. When a file DOES carry details it produces rows instead (hasData),
  // and its policy rides with the location the operator assigns, so there's no separate scope entry.
  const isOrgWide = !!upload && upload.locationIds === null;
  const policyGroups = changes.policyGroups;
  const policyNeedsReview = changes.policyNeedsReview;
  // A row that hasn't been matched to a location yet can carry policy findings too (the scan
  // clones the document's policy onto every roster row, matched or not) — but they can't be
  // diffed/grouped without a location to diff against, so they're invisible to policyGroups.
  // Keep the Policies step (and its "assign a location first" notice) around for them, rather
  // than letting the step vanish the moment every matched location's own diffs settle to zero.
  const unmatchedPolicyRows = changes.unmatched.filter((row) => row.policyCount > 0 && !row.dismissed);
  const hasPolicy = policyGroups.length > 0 || policyNeedsReview.length > 0 || unmatchedPolicyRows.length > 0;
  // Same location matched on two rows that still disagree on a field — blocked from applying
  // (server-side, regardless of whether this banner is ever seen) until the operator picks one.
  const conflicts = changes.conflicts;
  const matchedCount = changes.locations.length;
  // Dismissed rows aren't "waiting for later" — the operator said they'll never match a location —
  // so they're excluded from the pending count and shown in their own collapsed section instead.
  const unmatchedCount = changes.unmatched.filter((row) => !row.dismissed).length;
  const dismissedRows = changes.unmatched.filter((row) => row.dismissed);
  const dismissedLocations = changes.dismissedLocations;
  // Dismissed fields explicitly won't change — never counted here, same as an already-applied one.
  const changedCount = changes.locations.reduce(
    (sum, location) => sum + location.data.filter((change) => change.changed && !change.excluded).length,
    0
  );
  const scopePolicy = policyGroups.some((group) => group.scope === "org");

  // Approve-all confirm copy: how many fields/locations this scope's single call would actually
  // still write (already-applied or dismissed ones aren't "about to update" anything).
  const pendingDataCount = grid.rows.reduce(
    (sum, row) => sum + [...row.cells.values()].filter((cell) => cell.changed && !cell.appliedAt && !cell.excluded).length,
    0
  );
  const pendingDataLocationCount = grid.rows.filter(
    (row) => row.locationId !== null && [...row.cells.values()].some((cell) => cell.changed && !cell.appliedAt && !cell.excluded)
  ).length;
  const pendingPolicyGroups = policyGroups.filter((group) => !(group.scope === "org" ? group.orgApplied : group.applied));
  const pendingPolicyCount = pendingPolicyGroups.length;
  const pendingPolicyLocationCount = new Set(
    pendingPolicyGroups.flatMap((group) => group.locations.filter((location) => !location.excluded).map((location) => location.locationId))
  ).size;

  if (!hasData && !hasPolicy) {
    return (
      <div className="px-8 py-6">
        <BackLink onClick={back} />
        <div className="mx-auto max-w-lg py-16 text-center">
          {scanFailed ? (
            <>
              <h1 className="text-xl font-semibold text-grey-900">Something went wrong on our end while scanning this file</h1>
              <p className="mt-2 text-sm text-grey-600">The file itself is fine, so retrying the scan usually works.</p>
              <div className="mt-4">
                <Button color={ButtonColor.ORANGE} leftIcon={<FiRefreshCw />} onClick={handleRetryScan}>
                  Retry scan
                </Button>
              </div>
            </>
          ) : (
            <>
              <h1 className="text-xl font-semibold text-grey-900">I’m sorry, we couldn’t find any information from that document</h1>
              <p className="mt-2 text-sm text-grey-600">
                Try a document that lists details per location: a location roster with phone numbers and addresses, a lease agreement, or your policy
                pages tend to work best.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (applied) {
    const { groups, locations } = computeWhatChanged(changes);
    // Sum of computeWhatChanged's own per-family counts, which already count only what was
    // actually applied — distinct from the page-wide `changedCount` below (still-pending diffs).
    const appliedFieldCount = groups.reduce((sum, group) => sum + group.fields, 0);
    return (
      <DoneScreen
        appliedFieldCount={appliedFieldCount}
        unmatchedCount={unmatchedCount}
        hasUnapproved={pendingDataCount > 0 || pendingPolicyCount > 0 || unmatchedCount > 0}
        groups={groups}
        locations={locations}
        onDone={back}
        onGoToPropertyManager={goToPropertyManager}
        onKeepReviewing={keepReviewing}
      />
    );
  }

  // Content-dependent step list — steps this document has EVER needed (seenSteps), not just what's
  // currently pending, so approving everything in a step doesn't make it (and the rail position)
  // disappear out from under the operator mid-review. `seenSteps` only latches in via an effect
  // (fires after commit), so it's still empty on the very first render where `hasData`/`hasPolicy`
  // become true — ORing them in here covers that one-render gap; without it `steps` is briefly `[]`
  // and `steps[safeIndex]` below throws (this used to be masked by an unconditional "apply" step
  // that always kept `steps` non-empty, before that step was removed).
  const steps: FlowStep[] = [];
  if (seenSteps.has("details") || hasData) steps.push({ key: "details", label: "Location details" });
  if (seenSteps.has("policies") || hasPolicy) steps.push({ key: "policies", label: "Policies" });

  const safeIndex = Math.min(stepIndex, steps.length - 1);
  const current = steps[safeIndex].key;
  const isLastStep = safeIndex === steps.length - 1;
  const goNext = (): void => setStepIndex(Math.min(safeIndex + 1, steps.length - 1));
  const goTo = (index: number): void => setStepIndex(index);
  const continueLabel = isLastStep ? "Done" : "Continue to policies";
  const detailsStepIndex = steps.findIndex((step) => step.key === "details");

  return (
    <div className="min-h-screen bg-white">
      <div className="sticky top-0 z-40 border-b border-grey-200 bg-white">
        <div className="mx-auto max-w-[1360px] px-8 pt-4">
          <div className="flex items-center justify-between">
            <BackLink onClick={back} />
            <div className="flex items-center gap-2 text-[13px] text-grey-600">
              {upload?.source && <span className="font-medium text-grey-800">{upload.source}</span>}
              <span className="h-3.5 w-px bg-grey-300" />
              <span>
                {isOrgWide ? "All locations" : `${matchedCount} location${matchedCount === 1 ? "" : "s"}`}
                {policyGroups.length > 0 ? ` · ${policyGroups.length} to review` : ""}
                {unmatchedPolicyRows.length > 0
                  ? ` · ${unmatchedPolicyRows.length} location${unmatchedPolicyRows.length === 1 ? "" : "s"} pending assignment`
                  : ""}
                {conflicts.length > 0 ? ` · ${conflicts.length} conflict${conflicts.length === 1 ? "" : "s"} to resolve` : ""}
              </span>
            </div>
          </div>
          <div className="pt-3 pb-3.5">
            <StepRail steps={steps} current={safeIndex} onStep={goTo} />
          </div>
        </div>
      </div>

      {/* Extra bottom padding — a bottom-right, fixed-position toast (see AlertsModel) has covered
          the footer's action button often enough to matter (right after acting on a row near the
          bottom of a short page); keeping the footer from ever sitting flush with the viewport's
          bottom edge is a page-scoped fix, not a global toast-position change. */}
      <div className="mx-auto max-w-[1360px] px-8 pt-8 pb-24">
        {scanFailed && (
          <div className="mb-6 flex items-center justify-between gap-4 rounded-[5px] border border-red-200 bg-red-50 px-5 py-3">
            <div className="flex items-center gap-2.5">
              <FiAlertCircle className="size-4 shrink-0 text-red-500" />
              <p className="text-[13px] font-semibold text-grey-900">
                The scan failed on our end, so policies and some location details are missing below. The file itself is fine; retry the scan to get
                the full results.
              </p>
            </div>
            <Button color={ButtonColor.WHITE} size={ButtonSize.MEDIUM} leftIcon={<FiRefreshCw />} onClick={handleRetryScan}>
              Retry scan
            </Button>
          </div>
        )}
        {current === "details" && (
          <DetailsScreen
            grid={grid}
            locationOptions={locationOptions}
            onAssign={handleAssign}
            onEdit={handleEditFinding}
            matchedCount={matchedCount}
            changedCount={changedCount}
            continueLabel={continueLabel}
            onContinue={isLastStep ? handleFinish : goNext}
            view={pickedView ?? "byLocation"}
            onView={setPickedView}
            resolvingConflictKeys={resolvingConflictKeys}
            onResolveConflict={handleResolveConflict}
            dismissedRows={dismissedRows}
            dismissedLocations={dismissedLocations}
            dismissingRows={dismissingRows}
            onDismissRow={handleDismissRow}
            onApprove={handleApproveLocation}
            approving={approving}
            onDismissFinding={handleDismissFinding}
            pendingCount={pendingDataCount}
            onOpenApproveAll={() => setApproveAllScope("data")}
            unmatchedCount={unmatchedCount}
            finishing={isLastStep ? applying : false}
          />
        )}
        {current === "policies" && (
          <PoliciesScreen
            groups={policyGroups}
            needsReview={policyNeedsReview}
            onFixNeedsReview={handleFixNeedsReview}
            unmatchedPolicyRows={unmatchedPolicyRows}
            onGoToDetails={detailsStepIndex >= 0 ? () => goTo(detailsStepIndex) : undefined}
            allLocationOptions={allLocationOptions}
            handlers={policyHandlers}
            pendingCount={pendingPolicyCount}
            onOpenApproveAll={() => setApproveAllScope("policy")}
            onBack={seenSteps.has("details") ? () => goTo(safeIndex - 1) : undefined}
            onContinue={handleFinish}
            finishing={applying}
          />
        )}
      </div>

      {approveAllScope && (
        <ApproveAllConfirmModal
          count={approveAllScope === "data" ? pendingDataCount : pendingPolicyCount}
          locationCount={approveAllScope === "data" ? pendingDataLocationCount : pendingPolicyLocationCount}
          orgWide={approveAllScope === "policy" && scopePolicy}
          approving={approving}
          onCancel={() => setApproveAllScope(null)}
          onConfirm={approveAllScope === "data" ? handleApproveAllData : handleApproveAllPolicy}
        />
      )}
    </div>
  );
}

function BackLink({ onClick }: { onClick: () => void }): React.ReactElement {
  return (
    <button onClick={onClick} className="flex items-center gap-1.5 text-sm font-semibold text-grey-600 transition-colors hover:text-grey-900">
      <FiArrowLeft className="size-4" /> Back to Document Upload
    </button>
  );
}

interface ApproveAllConfirmModalProps {
  /** Fields/updates this call would actually still write. */
  count: number;
  locationCount: number;
  /** True for an org-wide policy-only document — no per-location count to show. */
  orgWide: boolean;
  approving: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}

// Local to this page, not a new shared libs/ui component — generalizing LeaveConfirmModal's shape
// for one new consumer is a drive-by refactor per AGENTS.md §2; revisit if a third consumer appears.
function ApproveAllConfirmModal({ count, locationCount, orgWide, approving, onCancel, onConfirm }: ApproveAllConfirmModalProps): React.ReactElement {
  const scopeText = orgWide ? "your organization" : `${locationCount} location${locationCount === 1 ? "" : "s"}`;
  return (
    <Modal isOpen onClose={(open) => !open && onCancel()} title="Approve all" size={ModalSize.MEDIUM}>
      <div className="flex flex-col gap-4 px-4 pb-4">
        <p className="text-sm text-grey-900">
          You’re about to update <strong>{count}</strong> thing{count === 1 ? "" : "s"} across <strong>{scopeText}</strong>. Continue?
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant={ButtonVariant.GHOST} onClick={onCancel} disabled={approving}>
            Cancel
          </Button>
          <Button variant={ButtonVariant.PRIMARY} color={ButtonColor.ORANGE} onClickAsync={onConfirm} disabled={approving}>
            Approve all
          </Button>
        </div>
      </div>
    </Modal>
  );
}

interface StepRailProps {
  steps: FlowStep[];
  current: number;
  onStep: (index: number) => void;
}

/** Content-dependent step rail. Completed steps are teal + clickable; current is orange; upcoming muted. */
function StepRail({ steps, current, onStep }: StepRailProps): React.ReactElement {
  return (
    <div className="flex items-center gap-2">
      {steps.map((step, index) => {
        const done = index < current;
        const isCurrent = index === current;
        return (
          <Fragment key={step.key}>
            {index > 0 && <span className={filterClasses("h-px w-10", done || index <= current ? "bg-teal-800" : "bg-grey-200")} />}
            <button
              type="button"
              disabled={!done}
              onClick={() => done && onStep(index)}
              className={filterClasses("flex items-center gap-2", done && "cursor-pointer")}
            >
              <span
                className={filterClasses(
                  "flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                  done ? "bg-teal-800 text-white" : isCurrent ? "bg-orange-500 text-white" : "bg-grey-100 text-grey-500"
                )}
              >
                {done ? <FiCheck className="size-3.5" /> : index + 1}
              </span>
              <span
                className={filterClasses(
                  "text-sm",
                  isCurrent ? "font-bold text-grey-900" : done ? "font-semibold text-grey-700" : "font-medium text-grey-400"
                )}
              >
                {step.label}
              </span>
            </button>
          </Fragment>
        );
      })}
    </div>
  );
}

// Field-group categories are DERIVED from the findings' kinds — never a hardcoded column
// list. Only categories with ≥1 present column render as a tab; a single category shows no tabs.
const CATEGORY_ORDER = ["Contact", "Hours", "Amenities"] as const;
type DetailCategory = (typeof CATEGORY_ORDER)[number];

// Two views of the same data, chosen by a toggle (no tabs, no pagination): "byLocation" ("Walk
// through") is a diff-first stack of per-location cards; "all" ("Compare all") is the wide
// spreadsheet. Always defaults to Walk through; the operator's explicit pick wins after.
type DetailsView = "byLocation" | "all";

function categoryFor(kind: BulkChangeKind): DetailCategory {
  if (kind === "schedule") return "Hours";
  if (kind === "amenity" || kind === "service" || kind === "storage_type") return "Amenities";
  return "Contact"; // info
}

interface CellEdit {
  editingKey: string | null;
  draft: string;
  saving: boolean;
  onDraft: (value: string) => void;
  onStart: (rowKey: string, mapKey: string, initial: string) => void;
  onEscape: () => void;
  onBlur: (locationId: number, mapKey: string) => void;
  onToggle: (locationId: number, mapKey: string, value: string) => void;
  /** Dismiss (or restore) this one field — e.g. a lease's signing-entity info wrongly read as the
   * facility's own. Never written; stays visible grayed with an undo. */
  onDismiss: (locationId: number, mapKey: string, excluded: boolean) => void;
}

interface DetailsScreenProps {
  grid: { columns: Column[]; rows: GridRow[] };
  locationOptions: SelectItem[];
  onAssign: (rowIndex: number, value: string) => void;
  onEdit: (locationId: number, mapKey: string, value: string) => Promise<void>;
  matchedCount: number;
  changedCount: number;
  continueLabel: string;
  onContinue: () => void;
  /** Current details view: the per-location card stack, or the wide compare-all spreadsheet. */
  view: DetailsView;
  onView: (view: DetailsView) => void;
  /** `${locationId}::${mapKey}` keys mid-resolve (button disabled + spinner-ish state). */
  resolvingConflictKeys: Set<string>;
  onResolveConflict: (locationId: number, mapKey: string, keepValue: string) => void;
  /** Rows dismissed as "not one of our locations" — shown collapsed, with a restore action. */
  dismissedRows: BulkUnmatchedRow[];
  /** Matched locations the operator dropped (Compare-all's "Drop") — shown collapsed, restorable. */
  dismissedLocations: BulkChanges["dismissedLocations"];
  dismissingRows: Set<number>;
  onDismissRow: (rowIndex: number, dismissed: boolean) => void;
  onApprove: (locationId: number) => Promise<void>;
  approving: boolean;
  /** Dismiss (or restore) a location's detail change(s) — one field (mapKey given) or "Dismiss all
   * details" for that location at once (mapKey omitted). */
  onDismissFinding: (locationId: number, excluded: boolean, mapKey?: string) => void;
  /** Fields not yet approved — drives whether "Approve all" shows at all. */
  pendingCount: number;
  onOpenApproveAll: () => void;
  /** Rows still needing a location (not dismissed) — every one must be resolved (matched or
   * dropped) before continuing; 0 unlocks the footer's Continue/Done button. */
  unmatchedCount: number;
  /** True while onContinue's call is in flight — only meaningful when this is the last step (Done). */
  finishing?: boolean;
}

function DetailsScreen({
  grid,
  locationOptions,
  onAssign,
  onEdit,
  matchedCount,
  changedCount,
  continueLabel,
  onContinue,
  view,
  onView,
  resolvingConflictKeys,
  onResolveConflict,
  dismissedRows,
  dismissedLocations,
  dismissingRows,
  onDismissRow,
  onApprove,
  approving,
  onDismissFinding,
  pendingCount,
  onOpenApproveAll,
  unmatchedCount,
  finishing = false
}: DetailsScreenProps): React.ReactElement {
  // Compare-all groups columns by the categories actually present.
  const presentCategories = CATEGORY_ORDER.filter((category) => grid.columns.some((column) => categoryFor(column.kind) === category));

  // Inline edit: click a value → input; Enter or blur commits, Escape cancels. Commit is a single
  // path (on blur); Enter blurs, Escape flags a skip then blurs, so the trailing blur doesn't re-commit.
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [original, setOriginal] = useState("");
  const [saving, setSaving] = useState(false);
  const skipCommit = useRef(false);

  const cellEdit: CellEdit = {
    editingKey,
    draft,
    saving,
    onDraft: setDraft,
    onStart: (rowKey, mapKey, initial) => {
      skipCommit.current = false;
      setEditingKey(`${rowKey}:${mapKey}`);
      setDraft(initial);
      setOriginal(initial);
    },
    onEscape: () => {
      skipCommit.current = true;
    },
    onBlur: async (locationId, mapKey) => {
      setEditingKey(null);
      if (skipCommit.current || draft.trim() === original.trim()) {
        skipCommit.current = false;
        return;
      }
      setSaving(true);
      await onEdit(locationId, mapKey, draft);
      setSaving(false);
    },
    onToggle: (locationId, mapKey, value) => {
      // Staged only — written on the final Apply, like every other edit.
      void onEdit(locationId, mapKey, value);
    },
    onDismiss: (locationId, mapKey, excluded) => onDismissFinding(locationId, excluded, mapKey)
  };

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-3xl font-semibold text-grey-900">Review location details</h1>

      {unmatchedCount > 0 && (
        <div className="flex items-center gap-2.5 rounded-[5px] border border-orange-200 bg-orange-50 px-5 py-3">
          <FiAlertCircle className="size-4 shrink-0 text-orange-500" />
          <p className="text-[13px] font-semibold text-grey-900">
            {unmatchedCount} location{unmatchedCount === 1 ? "" : "s"} couldn’t be automatically matched. Please review{" "}
            {unmatchedCount === 1 ? "it" : "them"} below.
          </p>
        </div>
      )}

      <div className="flex items-end justify-between gap-4">
        <div className="min-w-0 pb-0.5">
          {pendingCount > 0 && (
            <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} onClick={onOpenApproveAll}>
              Approve all
            </Button>
          )}
        </div>
        <ViewToggle view={view} onView={onView} />
      </div>

      {view === "all" ? (
        <AllFieldsTable
          grid={grid}
          categories={presentCategories}
          locationOptions={locationOptions}
          onAssign={onAssign}
          edit={cellEdit}
          resolvingConflictKeys={resolvingConflictKeys}
          onResolveConflict={onResolveConflict}
          onApprove={onApprove}
          approving={approving}
          onDismissRow={onDismissRow}
          dismissingRows={dismissingRows}
          onDismissFinding={onDismissFinding}
        />
      ) : (
        <ByLocationView
          grid={grid}
          locationOptions={locationOptions}
          onAssign={onAssign}
          edit={cellEdit}
          resolvingConflictKeys={resolvingConflictKeys}
          onResolveConflict={onResolveConflict}
          onDismissRow={onDismissRow}
          onApprove={onApprove}
          approving={approving}
          onDismissFinding={onDismissFinding}
        />
      )}

      {dismissedRows.length > 0 && (
        <DismissedRowsSection
          title="Dismissed"
          items={dismissedRows.map((row) => ({ rowIndex: row.rowIndex, label: row.identity.name?.trim() || "Unmatched row" }))}
          dismissingRows={dismissingRows}
          onRestore={(rowIndex) => onDismissRow(rowIndex, false)}
        />
      )}

      {dismissedLocations.length > 0 && (
        <DismissedRowsSection
          title="Dropped locations"
          items={dismissedLocations.map((location) => ({ rowIndex: location.rowIndex, label: location.locationName }))}
          dismissingRows={dismissingRows}
          onRestore={(rowIndex) => onDismissRow(rowIndex, false)}
        />
      )}

      <FooterBar
        title={
          changedCount > 0
            ? `${changedCount} field${changedCount === 1 ? "" : "s"} will change across ${matchedCount} location${matchedCount === 1 ? "" : "s"}`
            : "No values will change"
        }
        caption={
          unmatchedCount > 0
            ? `Assign a location or drop the row for the ${unmatchedCount} remaining row${unmatchedCount === 1 ? "" : "s"} before continuing.`
            : "Changes are saved as you approve them."
        }
        action={
          <Button color={ButtonColor.ORANGE} onClick={onContinue} disabled={unmatchedCount > 0} loading={finishing}>
            {continueLabel}
          </Button>
        }
      />
    </div>
  );
}

interface ByLocationViewProps {
  grid: { columns: Column[]; rows: GridRow[] };
  locationOptions: SelectItem[];
  onAssign: (rowIndex: number, value: string) => void;
  edit: CellEdit;
  resolvingConflictKeys: Set<string>;
  onResolveConflict: (locationId: number, mapKey: string, keepValue: string) => void;
  onDismissRow: (rowIndex: number, dismissed: boolean) => void;
  onApprove: (locationId: number) => Promise<void>;
  approving: boolean;
  onDismissFinding: (locationId: number, excluded: boolean, mapKey?: string) => void;
}

// The diff-first stack: one card per location, each showing what changes there. No table, no
// pagination — horizontal detail lives in the Compare-all view instead.
function ByLocationView({
  grid,
  locationOptions,
  onAssign,
  edit,
  resolvingConflictKeys,
  onResolveConflict,
  onDismissRow,
  onApprove,
  approving,
  onDismissFinding
}: ByLocationViewProps): React.ReactElement {
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewStartKey, setReviewStartKey] = useState<string | null>(null);

  const openReviewAt = (key: string): void => {
    setReviewStartKey(key);
    setReviewOpen(true);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3">
        {grid.rows.map((row) => (
          <LocationCard
            key={row.key}
            row={row}
            columns={grid.columns}
            locationOptions={locationOptions}
            onAssign={onAssign}
            edit={edit}
            onReview={() => openReviewAt(row.key)}
            resolvingConflictKeys={resolvingConflictKeys}
            onResolveConflict={onResolveConflict}
            onDismissRow={onDismissRow}
            onApprove={onApprove}
            approving={approving}
            onDismissFinding={onDismissFinding}
          />
        ))}
      </div>

      {reviewOpen && (
        <LocationWalkThrough
          grid={grid}
          edit={edit}
          onApprove={onApprove}
          approving={approving}
          onDismissFinding={onDismissFinding}
          startRowKey={reviewStartKey}
          onClose={() => setReviewOpen(false)}
        />
      )}
    </div>
  );
}

// A feature-kind cell (amenity/service/storage_type) stays VISIBLE whenever the document proposed
// any value for it, even once it matches today's stored answer — otherwise toggling one back to
// match today's value makes it (and, if it was the only diff, the whole card) silently disappear
// right after the click that was supposed to edit it. `realChanges` stays scoped to REAL diffs only,
// so this never inflates what's actually pending (the Review modal deliberately uses `realChanges`
// alone instead, to keep its per-location step from growing unbounded — see LocationWalkThrough).
function changedColumnsFor(row: GridRow, columns: Column[], unmatched: boolean): { visible: Column[]; realChanges: Column[] } {
  const visible: Column[] = [];
  const realChanges: Column[] = [];
  for (const column of columns) {
    const cell = row.cells.get(column.mapKey);
    if (!cell) continue;
    if (cell.changed || unmatched) realChanges.push(column);
    if (cell.changed || unmatched || isFeatureKind(column.kind)) visible.push(column);
  }
  return { visible, realChanges };
}

// Group a KIND-ordered column list into contiguous category bands (Contact/Hours/Amenities). Since
// the columns are already sorted by KIND_ORDER, each category is contiguous → one band each.
function bandChanges(cols: Column[]): { category: DetailCategory; cols: Column[] }[] {
  const bands: { category: DetailCategory; cols: Column[] }[] = [];
  for (const col of cols) {
    const category = categoryFor(col.kind);
    const last = bands[bands.length - 1];
    if (last && last.category === category) last.cols.push(col);
    else bands.push({ category, cols: [col] });
  }
  return bands;
}

interface LocationCardProps {
  row: GridRow;
  columns: Column[];
  locationOptions: SelectItem[];
  onAssign: (rowIndex: number, value: string) => void;
  edit: CellEdit;
  /** Opens the location-level Walk Through modal (LocationWalkThrough), starting at this row. */
  onReview: () => void;
  resolvingConflictKeys: Set<string>;
  onResolveConflict: (locationId: number, mapKey: string, keepValue: string) => void;
  onDismissRow: (rowIndex: number, dismissed: boolean) => void;
  onApprove: (locationId: number) => Promise<void>;
  approving: boolean;
  onDismissFinding: (locationId: number, excluded: boolean, mapKey?: string) => void;
}

// One location's card: header (name + change count + Review) plus, when relevant, a conflicts
// banner or an assign strip — the changed-fields list itself lives only in the Review modal now
// (LocationWalkThrough), never inline on the card.
function LocationCard({
  row,
  columns,
  locationOptions,
  onAssign,
  edit,
  onReview,
  resolvingConflictKeys,
  onResolveConflict,
  onDismissRow,
  onApprove,
  approving,
  onDismissFinding
}: LocationCardProps): React.ReactElement {
  const [reassigning, setReassigning] = useState(false);
  const unmatched = row.locationName === null;
  const editable = row.locationId !== null;
  const { realChanges } = changedColumnsFor(row, columns, unmatched);

  const changeCount = realChanges.length;
  const showAssign = row.rowIndex !== null && (unmatched || reassigning);
  const showApprove = editable && hasUnapprovedChange(row.cells);
  // Best-guess suggestion (a near-miss fuzzy match, or an LLM-suggested semantic match) — never
  // auto-applied, but a one-click way to accept it instead of searching the full location list.
  const topAlternative = unmatched ? row.alternatives[0] : undefined;
  // Address context under the name, for telling same-named locations apart — suppressed when the
  // address itself is a proposed change (that change row already says it, no need to repeat it).
  const addressSubline = !hasAddressChange(row.cells) ? [row.city, row.state].filter(Boolean).join(", ") : "";

  return (
    <div className="overflow-hidden rounded-[5px] border border-grey-200 bg-white shadow-panel">
      <div
        onClick={editable ? onReview : undefined}
        className={filterClasses(
          "flex items-center justify-between gap-4 border-b border-grey-200 px-5 py-3.5",
          editable && "cursor-pointer hover:bg-grey-50"
        )}
      >
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-[17px] font-semibold text-grey-900">{row.locationName ?? row.readName ?? "Unmatched location"}</span>
            {topAlternative && (
              <span className="flex shrink-0 items-center gap-1.5">
                <span className="truncate text-[12px] text-grey-500">Suggested: {topAlternative.name}</span>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onAssign(row.rowIndex as number, `loc:${topAlternative.locationId}`);
                  }}
                  className="shrink-0 rounded-[4px] border border-teal-200 bg-teal-50 px-2 py-0.5 text-[12px] font-semibold text-teal-700 transition-colors hover:bg-teal-100"
                >
                  Confirm location
                </button>
              </span>
            )}
            {row.conflicts.length > 0 && (
              <Tooltip
                content={`${row.conflicts.length} field${row.conflicts.length === 1 ? "" : "s"} conflict: the document matched this location twice with different answers`}
              >
                <FiAlertCircle className="size-4 shrink-0 text-red-500" />
              </Tooltip>
            )}
            {!unmatched && row.rowIndex !== null && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setReassigning((value) => !value);
                }}
                aria-label="Change location"
                title="Change location"
                className="shrink-0 rounded-full p-1 text-grey-400 transition-colors hover:bg-grey-100 hover:text-grey-900"
              >
                <FiRefreshCw className="size-3.5" />
              </button>
            )}
          </div>
          {addressSubline && <span className="truncate text-[12px] text-grey-500">{addressSubline}</span>}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {!row.reviewed && <CountPill count={changeCount} />}
          {row.reviewed ? (
            <span className="text-[13px] font-semibold text-orange-600">Reviewed</span>
          ) : (
            showApprove && (
              <>
                {/* Bulk "dismiss all details" — e.g. a lease's signing-entity info wrongly read as
                    the facility's own. A per-field undo lives on each field inside Review instead. */}
                <button
                  type="button"
                  disabled={approving}
                  title="Dismiss every detail change found for this location"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDismissFinding(row.locationId as number, true);
                  }}
                  className="text-[13px] font-semibold text-grey-500 transition-colors hover:text-grey-900 disabled:opacity-50"
                >
                  Dismiss details
                </button>
                <button
                  type="button"
                  disabled={approving}
                  onClick={(event) => {
                    event.stopPropagation();
                    onApprove(row.locationId as number);
                  }}
                  className="rounded-[4px] border border-orange-200 bg-orange-50 px-2.5 py-1 text-[13px] font-semibold text-orange-700 transition-colors hover:bg-orange-100 disabled:opacity-50"
                >
                  Approve
                </button>
              </>
            )
          )}
          {editable && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onReview();
              }}
              className="text-[13px] font-semibold text-grey-500 transition-colors hover:text-grey-900"
            >
              Review
            </button>
          )}
        </div>
      </div>

      {row.conflicts.length > 0 && (
        <div className="border-b border-grey-200 bg-red-50 px-5 py-3">
          <ConflictResolutionList conflicts={row.conflicts} resolvingKeys={resolvingConflictKeys} onResolve={onResolveConflict} />
        </div>
      )}

      {showAssign && (
        <div
          className={filterClasses(
            "flex items-center gap-3 border-b border-grey-200 px-5 py-3.5 last:border-b-0",
            unmatched ? "bg-orange-50" : "bg-grey-50"
          )}
        >
          <span className="shrink-0 text-[13px] font-semibold text-grey-900">{unmatched ? "No location matched" : "Change location"}</span>
          <div className="w-full max-w-sm min-w-[240px]">
            <SearchSelect
              selectedValues={[]}
              options={locationOptions}
              onChange={(values) => {
                if (values[0]) {
                  onAssign(row.rowIndex as number, values[0]);
                  setReassigning(false);
                }
              }}
              placeholder="Assign a location…"
              hasSearchInDropdown
            />
          </div>
          {unmatched && (
            <button
              type="button"
              onClick={() => onDismissRow(row.rowIndex as number, true)}
              title="This isn't one of our locations"
              className="shrink-0 text-[13px] font-semibold text-grey-500 transition-colors hover:text-grey-900"
            >
              Drop
            </button>
          )}
        </div>
      )}

      {unmatched && row.policyCount > 0 && (
        <div className="border-b border-grey-200 bg-orange-50 px-5 py-2.5 text-[13px] text-orange-700 last:border-b-0">
          Also carries {row.policyCount} policy update{row.policyCount === 1 ? "" : "s"}; they won't show up under Policies until this location is
          assigned.
        </div>
      )}
    </div>
  );
}

interface LocationWalkThroughProps {
  grid: { columns: Column[]; rows: GridRow[] };
  edit: CellEdit;
  onApprove: (locationId: number) => Promise<void>;
  approving: boolean;
  onDismissFinding: (locationId: number, excluded: boolean, mapKey?: string) => void;
  /** null = default to the first not-yet-reviewed location, or the first if everything's reviewed. */
  startRowKey: string | null;
  onClose: () => void;
}

// The location-level mirror of Policies' Walk Through: one matched location at a time, left rail +
// progress, "Approve & Next". Reuses ChangeRow/GroupBand/bandChanges — the exact same per-field
// rendering LocationCard used to show inline, just moved here. Deliberately shows ONLY real changes
// (never a matches-today feature field kept visible to protect a toggle-edit — see
// changedColumnsFor's comment) so a step's length can never grow unbounded across many locations;
// the tradeoff is that toggling a feature field back to its original value can make that row
// disappear from the CURRENT step before moving on — accepted, not an oversight.
function LocationWalkThrough({
  grid,
  edit,
  onApprove,
  approving,
  onDismissFinding,
  startRowKey,
  onClose
}: LocationWalkThroughProps): React.ReactElement {
  const steps = grid.rows.filter((row) => row.locationId !== null);
  const indexByKey = new Map(steps.map((row, i) => [row.key, i]));

  const startIndex = useMemo(() => {
    if (startRowKey) {
      const found = indexByKey.get(startRowKey);
      if (found !== undefined) return found;
    }
    const firstUnreviewed = steps.findIndex((row) => !row.reviewed);
    return firstUnreviewed >= 0 ? firstUnreviewed : 0;
    // Only the modal's initial open should pick a starting step — after that the operator drives navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [index, setIndex] = useState(startIndex);

  const reviewedCount = steps.filter((row) => row.reviewed).length;
  const safeIndex = Math.min(index, steps.length - 1);
  const row = steps[safeIndex];
  const isLast = safeIndex === steps.length - 1;
  const isFirst = safeIndex === 0;

  const goTo = (next: number): void => setIndex(Math.max(0, Math.min(next, steps.length - 1)));

  // Real changes only (per your call, to keep a step from growing unbounded across many
  // locations), but an edit that happens to land back on today's value must not vanish the row
  // out from under the operator mid-edit. Tracked here, not server-side: it only needs to survive
  // for this one modal session, and every field type commits through onToggle/onBlur.
  const [editedKeys, setEditedKeys] = useState<Set<string>>(new Set());

  if (!row) return <></>;

  const trackedEdit: CellEdit = {
    ...edit,
    onToggle: (locationId, mapKey, value) => {
      setEditedKeys((prev) => new Set(prev).add(`${row.key}:${mapKey}`));
      edit.onToggle(locationId, mapKey, value);
    },
    onBlur: (locationId, mapKey) => {
      setEditedKeys((prev) => new Set(prev).add(`${row.key}:${mapKey}`));
      edit.onBlur(locationId, mapKey);
    }
  };

  const { realChanges: strictChanges } = changedColumnsFor(row, grid.columns, false);
  const realChanges = grid.columns.filter((column) => strictChanges.includes(column) || editedKeys.has(`${row.key}:${column.mapKey}`));
  const canApproveHere = !row.reviewed && hasUnapprovedChange(row.cells);
  // Awaits the write before advancing/closing — closing on the last step used to fire the instant
  // the click happened, before the request even started, so a failure only ever surfaced as a toast
  // after the modal (which by then looked like it had finished) was already gone.
  const handleApproveAndNext = async (): Promise<void> => {
    await onApprove(row.locationId as number);
    if (isLast) onClose();
    else goTo(safeIndex + 1);
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      hasTitleBorder
      title="Review location details"
      className="flex h-[min(86vh,760px)] w-[1040px] max-w-[calc(100vw-64px)] flex-col overflow-hidden"
    >
      <div className="flex min-h-0 flex-1">
        <div className="flex w-[300px] shrink-0 flex-col border-r border-grey-200 bg-grey-50">
          <div className="border-b border-grey-200 px-5 pt-5 pb-3.5">
            <div className="font-poppins text-[17px] font-semibold text-grey-900">Locations</div>
            <div className="mt-1 text-[12px] text-grey-500 tabular-nums">
              Step {safeIndex + 1} of {steps.length} · {reviewedCount} reviewed
            </div>
            <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-grey-200">
              <div
                className="h-full rounded-full bg-orange-500 transition-all"
                style={{ width: `${steps.length === 0 ? 0 : (reviewedCount / steps.length) * 100}%` }}
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto py-2">
            {steps.map((step, i) => (
              <button
                key={step.key}
                type="button"
                onClick={() => goTo(i)}
                className={filterClasses(
                  "flex w-full items-center gap-2.5 px-5 py-2 text-left transition-colors hover:bg-grey-100",
                  i === safeIndex && "bg-white font-semibold text-grey-900 shadow-[inset_3px_0_0_var(--color-orange-500)]"
                )}
              >
                <span
                  className={filterClasses(
                    "flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold",
                    step.reviewed ? "bg-teal-800 text-white" : i === safeIndex ? "bg-orange-500 text-white" : "bg-grey-200 text-grey-500"
                  )}
                >
                  {step.reviewed ? <FiCheck className="size-3" /> : i + 1}
                </span>
                <span className="min-w-0 flex-1 truncate text-[13px]">{step.locationName ?? step.readName ?? "Unmatched location"}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="border-b border-grey-200 px-7 py-5">
            <div className="font-poppins text-[22px] font-semibold text-grey-900">{row.locationName ?? row.readName}</div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {realChanges.length === 0 ? (
              <div className="px-7 py-6 text-[13px] text-grey-500">Nothing changes at this location.</div>
            ) : (
              bandChanges(realChanges).map((band) => (
                <Fragment key={band.category}>
                  <GroupBand label={band.category} count={band.cols.length} />
                  {band.cols.map((column) => (
                    <ChangeRow key={column.mapKey} row={row} column={column} editable edit={trackedEdit} />
                  ))}
                </Fragment>
              ))
            )}
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-grey-200 bg-grey-50 px-7 py-4">
            <button
              type="button"
              disabled={isFirst}
              onClick={() => goTo(safeIndex - 1)}
              className={filterClasses(
                "flex items-center gap-1.5 text-[13px] font-semibold",
                isFirst ? "text-grey-400" : "text-grey-600 hover:text-grey-900"
              )}
            >
              <FiArrowLeft className="size-3.5" /> Previous
            </button>
            <div className="flex items-center gap-2">
              <Button color={ButtonColor.WHITE} onClick={isLast ? onClose : () => goTo(safeIndex + 1)} disabled={approving}>
                {isLast ? "Close" : "Skip for now"}
              </Button>
              {canApproveHere && (
                <>
                  {/* e.g. a lease's signing-entity info wrongly read as the facility's own — a
                      per-field undo lives on each field above instead. */}
                  <Button
                    color={ButtonColor.WHITE}
                    onClick={() => onDismissFinding(row.locationId as number, true)}
                    disabled={approving}
                    title="Dismiss every detail change found for this location"
                  >
                    Dismiss details
                  </Button>
                  <Button color={ButtonColor.ORANGE} onClickAsync={handleApproveAndNext} disabled={approving}>
                    {isLast ? "Approve & finish" : "Approve & next"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

interface DismissedItem {
  rowIndex: number;
  label: string;
}

interface DismissedRowsSectionProps {
  title: string;
  items: DismissedItem[];
  dismissingRows: Set<number>;
  onRestore: (rowIndex: number) => void;
}

// Collapsed-by-default list of rows the operator said aren't one of our locations, OR matched
// locations the operator dropped from the review (Compare-all's "Drop") — restorable, never gone for
// good. Mirrors LocationCard's own collapse affordance, just at the section level. Shared by both
// kinds of dismissal; only the title and label differ at the call site.
function DismissedRowsSection({ title, items, dismissingRows, onRestore }: DismissedRowsSectionProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-[5px] border border-grey-200 bg-grey-50">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center justify-between px-5 py-3 text-[13px] font-semibold text-grey-600 transition-colors hover:text-grey-900"
      >
        <span>
          {title} ({items.length})
        </span>
        <span className="text-grey-400">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="flex flex-col border-t border-grey-200">
          {items.map((item) => (
            <div key={item.rowIndex} className="flex items-center justify-between gap-3 border-b border-grey-200 px-5 py-2.5 last:border-b-0">
              <span className="truncate text-[13px] text-grey-600">{item.label}</span>
              <button
                type="button"
                disabled={dismissingRows.has(item.rowIndex)}
                onClick={() => onRestore(item.rowIndex)}
                className="shrink-0 text-[13px] font-semibold text-orange-500 transition-colors hover:text-orange-600 disabled:opacity-50"
              >
                Restore
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Count pill in a card header: orange when something changes, neutral grey when nothing does.
function CountPill({ count }: { count: number }): React.ReactElement {
  if (count === 0) {
    return (
      <span className="inline-flex h-[22px] items-center rounded-full bg-grey-100 px-2.5 text-[12px] font-bold whitespace-nowrap text-grey-500">
        No changes
      </span>
    );
  }
  return (
    <span className="inline-flex h-[22px] items-center rounded-full border border-orange-100 bg-orange-50 px-2.5 text-[12px] font-bold whitespace-nowrap text-orange-700">
      {count} change{count === 1 ? "" : "s"}
    </span>
  );
}

// A group subhead band inside a card (Contact/Hours/Amenities, or the trailing "Unchanged").
function GroupBand({ label, count }: { label: string; count: number }): React.ReactElement {
  return (
    <div className="flex items-center justify-between border-b border-grey-200 bg-grey-50 px-5 py-2">
      <span className="text-[10px] font-bold tracking-[0.06em] text-grey-700 uppercase">{label}</span>
      <span className="text-[11px] text-grey-500 tabular-nums">{count}</span>
    </div>
  );
}

interface ChangeRowProps {
  row: GridRow;
  column: Column;
  editable: boolean;
  edit: CellEdit;
}

// One changed field: label · today (struck) · → · incoming · dismiss/undo. Text fields open an
// inline input on click; select fields (e.g. Persona) get a native dropdown instead — never free
// text, so a value can't drift outside its own option list; amenities are booleans and toggle on
// click. A field empty today reads "Not set" rather than a struck blank. A dismissed field (e.g. a
// lease's signing-entity info wrongly read as the facility's own) stays visible, greyed, with an
// undo — never written, but never silently disappears either.
function ChangeRow({ row, column, editable, edit }: ChangeRowProps): React.ReactElement {
  const cell = row.cells.get(column.mapKey);
  const feature = isFeatureKind(column.kind);
  const isSelect = !feature && column.fieldType === "select" && !!column.selectOptions?.length;
  const value = cell?.value ?? "";
  const previous = cell?.previous ?? null;
  const excluded = !!cell?.excluded;
  const incoming = feature ? yesNoLabel(value) : isSelect ? selectValueLabel(column, value) : renderValue(column.kind, value);
  const isEditing = editable && !feature && !isSelect && !excluded && edit.editingKey === `${row.key}:${column.mapKey}`;
  // A feature field shown despite already matching today's value (see the comment on `visible`
  // above) — grey it out so it reads as "already set," not as a change like everything else here.
  const noOp = previous !== null && previous !== "" && value === previous;
  const onClick =
    editable && !excluded
      ? feature
        ? (): void => edit.onToggle(row.locationId as number, column.mapKey, value === "1" ? "0" : "1")
        : (): void => edit.onStart(row.key, column.mapKey, value)
      : undefined;

  return (
    <div
      className={filterClasses(
        "grid grid-cols-[150px_minmax(0,1fr)_18px_minmax(0,1.2fr)_24px] items-baseline gap-3 border-b border-grey-200 px-5 py-2.5 hover:bg-grey-50",
        excluded && "opacity-60"
      )}
    >
      <span className="pt-0.5 text-[13px] font-semibold text-grey-500">{column.label}</span>
      {previous === null || previous === "" ? (
        <span className="text-[14px] leading-[19px] text-grey-500 italic">Not set</span>
      ) : (
        <span className="text-[14px] leading-[19px] [overflow-wrap:anywhere] text-grey-500 line-through decoration-grey-400">
          {feature ? yesNoLabel(previous) : isSelect ? selectValueLabel(column, previous) : previous}
        </span>
      )}
      <span className="text-center text-[13px] text-orange-500">→</span>
      {excluded ? (
        <span className="text-[14px] leading-[19px] text-grey-500 italic">Dismissed, won’t apply</span>
      ) : isSelect ? (
        <FieldSelect
          column={column}
          value={value}
          disabled={!editable || edit.saving}
          onChange={(next) => edit.onToggle(row.locationId as number, column.mapKey, next)}
          className="h-8 w-full rounded-[4px] border border-grey-300 bg-white px-2 text-[14px] disabled:opacity-60"
        />
      ) : isEditing ? (
        <input
          autoFocus
          {...inputPropsFor(column.fieldType)}
          value={edit.draft}
          disabled={edit.saving}
          onChange={(event) => edit.onDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
            else if (event.key === "Escape") {
              edit.onEscape();
              event.currentTarget.blur();
            }
          }}
          onBlur={() => edit.onBlur(row.locationId as number, column.mapKey)}
          className="h-8 w-full rounded-[4px] border-2 border-orange-500 bg-orange-50 px-2.5 text-[14px] outline-none"
        />
      ) : (
        <span
          onClick={onClick}
          title={editable ? (feature ? "Click to toggle" : "Click to edit") : undefined}
          className={filterClasses(
            "text-[14px] leading-[19px] [overflow-wrap:anywhere]",
            noOp ? "text-grey-400" : "font-semibold text-grey-900",
            editable && "cursor-pointer"
          )}
        >
          {incoming}
        </span>
      )}
      {editable && (
        <Tooltip content={excluded ? "Restore this field" : "Dismiss this field — it won't be applied"}>
          <button
            type="button"
            onClick={() => edit.onDismiss(row.locationId as number, column.mapKey, !excluded)}
            className="flex text-grey-400 transition-colors hover:text-grey-700"
          >
            {excluded ? <FiRotateCcw className="size-3.5" /> : <FiX className="size-3.5" />}
          </button>
        </Tooltip>
      )}
    </div>
  );
}

interface ViewToggleProps {
  view: DetailsView;
  onView: (view: DetailsView) => void;
}

// Segmented "Compare all | Walk through" switch — always visible above the details content.
// Compare all renders first (left) regardless of which is current — a fixed reading order, not a
// most-recent/most-relevant one.
function ViewToggle({ view, onView }: ViewToggleProps): React.ReactElement {
  const option = (value: DetailsView, label: string): React.ReactElement => (
    <button
      type="button"
      onClick={() => onView(value)}
      className={filterClasses(
        "px-3 py-[5px] text-[13px] font-semibold whitespace-nowrap transition-colors",
        view === value ? "bg-grey-900 text-white" : "bg-white text-grey-700 hover:bg-grey-50"
      )}
    >
      {label}
    </button>
  );
  return (
    <div className="flex shrink-0 items-center gap-2">
      <span className="text-[12px] font-semibold text-grey-500">View</span>
      <div className="flex overflow-hidden rounded-[4px] border border-grey-300">
        {option("all", "Compare all")}
        {option("byLocation", "Walk through")}
      </div>
    </div>
  );
}

interface AllFieldsTableProps {
  grid: { columns: Column[]; rows: GridRow[] };
  categories: DetailCategory[];
  locationOptions: SelectItem[];
  onAssign: (rowIndex: number, value: string) => void;
  edit: CellEdit;
  resolvingConflictKeys: Set<string>;
  onResolveConflict: (locationId: number, mapKey: string, keepValue: string) => void;
  onApprove: (locationId: number) => Promise<void>;
  approving: boolean;
  onDismissRow: (rowIndex: number, dismissed: boolean) => void;
  dismissingRows: Set<number>;
  onDismissFinding: (locationId: number, excluded: boolean, mapKey?: string) => void;
}

// Column widths for the dense All-fields grid. Columns are dynamic, so sized by kind (amenities are
// narrow ✓/✗ marks, schedules mid, info wide) with a couple of overrides for common contact fields.
// Location is wider than the rest — name + city/state now stack on two lines underneath it.
const LOCATION_COL_W = 260;
function columnWidth(column: Column): number {
  if (isFeatureKind(column.kind)) return 78;
  if (column.kind === "schedule") return 170;
  if (column.mapKey === "address" || column.mapKey === "email") return 200;
  if (column.mapKey === "phone") return 130;
  return 160; // other cortex info
}

// Wide "All fields" grid: every field is a column, grouped under a two-tier sticky header, with a
// frozen location column. Horizontal scroll is expected — that's the trade this dense view makes.
function AllFieldsTable({
  grid,
  categories,
  locationOptions,
  onAssign,
  edit,
  resolvingConflictKeys,
  onResolveConflict,
  onApprove,
  approving,
  onDismissRow,
  dismissingRows,
  onDismissFinding
}: AllFieldsTableProps): React.ReactElement {
  const columns = grid.columns; // buildGrid sorts by KIND_ORDER then label, so groups are contiguous
  const groups = categories
    .map((category) => ({ category, cols: columns.filter((column) => categoryFor(column.kind) === category) }))
    .filter((group) => group.cols.length > 0);
  const groupStartKeys = new Set(groups.map((group) => group.cols[0]?.mapKey).filter(Boolean));

  // Row 2 sticks directly under row 1 — measure row 1's height rather than hardcoding it.
  const row1Ref = useRef<HTMLTableRowElement>(null);
  const [row1Height, setRow1Height] = useState(34);
  useEffect(() => {
    if (row1Ref.current) setRow1Height(row1Ref.current.offsetHeight);
  }, [groups.length, columns.length]);

  const tableWidth = LOCATION_COL_W + columns.reduce((sum, column) => sum + columnWidth(column), 0);

  // The table's own horizontal scrollbar is hidden (kept scrollable by wheel/trackpad/keyboard) in
  // favor of this always-visible bar living just below the bordered block — mirrored via scrollLeft
  // in both directions so dragging either one moves the table. Split into two single-axis overflow
  // containers (outer: vertical only, inner: horizontal only) rather than one shared scroller, which
  // is also what lets the sticky header (top, via the outer ancestor) and frozen column (left, via
  // the inner ancestor) keep sticking correctly on their own axis.
  const hScrollRef = useRef<HTMLDivElement>(null);
  const bottomBarRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div className="overflow-hidden rounded-md border border-grey-200 shadow-panel">
        <div className="max-h-[600px] overflow-x-hidden overflow-y-auto">
          <div
            ref={hScrollRef}
            onScroll={(event) => {
              if (bottomBarRef.current) bottomBarRef.current.scrollLeft = event.currentTarget.scrollLeft;
            }}
            style={{ scrollbarWidth: "none" }}
            className="overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:hidden"
          >
            <table className="table-fixed border-separate border-spacing-0" style={{ width: "100%", minWidth: tableWidth }}>
              <colgroup>
                <col style={{ width: LOCATION_COL_W }} />
                {columns.map((column) => (
                  <col key={column.mapKey} style={{ width: columnWidth(column) }} />
                ))}
                {/* No explicit width — absorbs any leftover space so the zebra striping and header
                    background fill the whole page instead of stopping dead at the last real column
                    (a fixed-layout table otherwise leaves a bare white gap when there are few columns). */}
                <col />
              </colgroup>
              <thead>
                <tr ref={row1Ref}>
                  <th
                    rowSpan={2}
                    className="sticky top-0 left-0 z-30 w-[260px] min-w-[260px] border-b border-grey-300 bg-grey-50 px-3 py-2 text-left text-[11px] font-bold tracking-[0.06em] text-grey-900 uppercase shadow-[1px_0_0_var(--color-grey-300)]"
                  >
                    Location
                  </th>
                  {groups.map((group) => (
                    <th
                      key={group.category}
                      colSpan={group.cols.length}
                      className="sticky top-0 z-20 border-b border-l border-grey-300 bg-grey-50 px-3 py-2 text-left text-[11px] font-bold tracking-[0.06em] text-grey-900 uppercase"
                    >
                      {group.category}
                    </th>
                  ))}
                  <th rowSpan={2} className="sticky top-0 z-20 border-b border-grey-300 bg-grey-50" />
                </tr>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.mapKey}
                      style={{ top: row1Height }}
                      title={column.label}
                      className={filterClasses(
                        "sticky z-20 border-b border-grey-300 bg-grey-50 px-3 py-2 text-[10px] font-bold tracking-[0.06em] [overflow-wrap:anywhere] text-grey-500 uppercase",
                        isFeatureKind(column.kind) ? "text-center" : "text-left",
                        groupStartKeys.has(column.mapKey) && "border-l"
                      )}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grid.rows.map((row, index) => (
                  <AllFieldsRow
                    key={row.key}
                    row={row}
                    index={index}
                    columns={columns}
                    groupStartKeys={groupStartKeys}
                    locationOptions={locationOptions}
                    onAssign={onAssign}
                    edit={edit}
                    resolvingConflictKeys={resolvingConflictKeys}
                    onResolveConflict={onResolveConflict}
                    onApprove={onApprove}
                    approving={approving}
                    onDismissRow={onDismissRow}
                    dismissingRows={dismissingRows}
                    onDismissFinding={onDismissFinding}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div
        ref={bottomBarRef}
        onScroll={(event) => {
          if (hScrollRef.current) hScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
        }}
        className="mt-1 h-4 overflow-x-auto overflow-y-hidden"
      >
        <div style={{ width: tableWidth, height: 1 }} />
      </div>
    </>
  );
}

interface AllFieldsRowProps {
  row: GridRow;
  index: number;
  columns: Column[];
  groupStartKeys: Set<string>;
  locationOptions: SelectItem[];
  onAssign: (rowIndex: number, value: string) => void;
  edit: CellEdit;
  resolvingConflictKeys: Set<string>;
  onResolveConflict: (locationId: number, mapKey: string, keepValue: string) => void;
  onApprove: (locationId: number) => Promise<void>;
  approving: boolean;
  onDismissRow: (rowIndex: number, dismissed: boolean) => void;
  dismissingRows: Set<number>;
  onDismissFinding: (locationId: number, excluded: boolean, mapKey?: string) => void;
}

// One location row in the wide grid: frozen location cell + a single-line value per field. The
// before→after detail moves into each cell's title (hover) so every row stays one line; a genuinely
// blank cell means the location didn't have that field. Zebra + orange hover give a spreadsheet feel.
function AllFieldsRow({
  row,
  index,
  columns,
  groupStartKeys,
  locationOptions,
  onAssign,
  edit,
  resolvingConflictKeys,
  onResolveConflict,
  onApprove,
  approving,
  onDismissRow,
  dismissingRows,
  onDismissFinding
}: AllFieldsRowProps): React.ReactElement {
  const editable = row.locationId !== null;
  const zebra = index % 2 === 1 ? "bg-grey-50" : "bg-white";
  return (
    <tr className="group">
      <td
        className={filterClasses(
          "sticky left-0 z-10 h-[38px] w-[260px] min-w-[260px] border-b border-grey-100 px-3 align-middle shadow-[1px_0_0_var(--color-grey-300)] group-hover:bg-orange-50",
          zebra
        )}
      >
        <LocationCell
          row={row}
          locationOptions={locationOptions}
          onAssign={onAssign}
          resolvingConflictKeys={resolvingConflictKeys}
          onResolveConflict={onResolveConflict}
          onApprove={onApprove}
          approving={approving}
          onDismiss={(rowIndex) => onDismissRow(rowIndex, true)}
          onDismissFinding={onDismissFinding}
          dismissing={row.rowIndex !== null && dismissingRows.has(row.rowIndex)}
        />
      </td>
      {columns.map((column) => {
        const cell = row.cells.get(column.mapKey);
        const feature = isFeatureKind(column.kind);
        const isSelect = !feature && column.fieldType === "select" && !!column.selectOptions?.length;
        const border = groupStartKeys.has(column.mapKey) ? "border-l border-grey-200" : "";
        const isEditing = editable && !isSelect && edit.editingKey === `${row.key}:${column.mapKey}`;

        // Select field → a native dropdown, never free text, so it can't drift outside its own
        // option list (the same fix as ChangeRow's, just always-visible here like a feature toggle).
        if (isSelect) {
          return (
            <td key={column.mapKey} className={filterClasses("h-[38px] border-b border-grey-100 p-0", zebra, border)}>
              <FieldSelect
                column={column}
                value={cell?.value ?? ""}
                disabled={!editable}
                onChange={(next) => edit.onToggle(row.locationId as number, column.mapKey, next)}
                className="h-[38px] w-full border-0 bg-transparent px-[10px] text-[13px] disabled:opacity-60"
              />
            </td>
          );
        }

        if (isEditing) {
          return (
            <td key={column.mapKey} className={filterClasses("h-[38px] border-b border-grey-100 p-0", zebra, border)}>
              <input
                autoFocus
                {...inputPropsFor(column.fieldType)}
                value={edit.draft}
                disabled={edit.saving}
                onChange={(event) => edit.onDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.currentTarget.blur();
                  else if (event.key === "Escape") {
                    edit.onEscape();
                    event.currentTarget.blur();
                  }
                }}
                onBlur={() => edit.onBlur(row.locationId as number, column.mapKey)}
                className="h-[38px] w-full border-2 border-orange-500 bg-orange-50 px-[10px] text-[13px] outline-none"
              />
            </td>
          );
        }

        // Amenity → orange ✓ (found) / faded ✗ (not offered); blank when not stated. Click flips it.
        if (feature) {
          const amenityValue = cell?.value ?? "";
          const known = amenityValue === "1" || amenityValue === "0";
          const on = amenityValue === "1";
          return (
            <td
              key={column.mapKey}
              onClick={editable ? () => edit.onToggle(row.locationId as number, column.mapKey, on ? "0" : "1") : undefined}
              title={known ? `${column.label}: ${on ? "found in document" : "not found"}` : undefined}
              className={filterClasses(
                "h-[38px] border-b border-grey-100 text-center align-middle group-hover:bg-orange-50",
                zebra,
                border,
                editable && "cursor-pointer"
              )}
            >
              {known ? (
                <span className={filterClasses("text-[15px] font-bold", on ? "text-orange-500" : "text-grey-900 opacity-[0.35]")}>
                  {on ? "✓" : "✗"}
                </span>
              ) : null}
            </td>
          );
        }

        // Text (info/schedule) → one line: bold if it differs from today, muted if it matches, blank
        // if nothing found. The before→after lives in the title.
        const value = cell?.value ?? "";
        const changed = !!cell?.changed;
        const rendered = cell ? renderValue(column.kind, cell.value) : "";
        const known = rendered !== "" && rendered !== "—";
        const title = !cell
          ? "Nothing found for this location, click to add"
          : changed
            ? `Today: ${cell.previous || "—"} → From document: ${rendered}`
            : undefined;
        return (
          <td
            key={column.mapKey}
            onClick={editable ? () => edit.onStart(row.key, column.mapKey, value) : undefined}
            title={title}
            className={filterClasses(
              "h-[38px] border-b border-grey-100 px-3 align-middle group-hover:bg-orange-50",
              zebra,
              border,
              editable && "cursor-text"
            )}
          >
            <div className={filterClasses("truncate text-[13px]", changed ? "font-semibold text-grey-900" : "font-normal text-grey-500")}>
              {known ? rendered : ""}
            </div>
          </td>
        );
      })}
      {/* Matches the filler <col> above — carries the zebra stripe out to the edge instead of it
          stopping dead after the last real column. */}
      <td className={filterClasses("h-[38px] border-b border-grey-100 group-hover:bg-orange-50", zebra)} />
    </tr>
  );
}

interface PoliciesScreenProps {
  groups: PolicyReviewGroup[];
  /** Findings the scan extracted but couldn't normalize into an accepted answer — invisible to
   * `groups` by construction (see PolicyNeedsReviewGroup's doc comment). Surfaced in its own card
   * list so they aren't silently dropped. */
  needsReview: PolicyNeedsReviewGroup[];
  onFixNeedsReview: (group: PolicyNeedsReviewGroup, newValue: string) => void;
  /** Rows the scan couldn't match to a location — their policy findings can't be diffed/reviewed
   * until one is assigned, so they're invisible to `groups`. Surfaced here as a blocking notice. */
  unmatchedPolicyRows: BulkUnmatchedRow[];
  /** Jumps to the Location details step, where an unmatched row gets a location assigned. */
  onGoToDetails?: () => void;
  /** Every org location, grouped by org group — for the "Affects N locations" multi-select. */
  allLocationOptions: SelectItem[];
  handlers: PolicyReviewHandlers;
  /** Updates not yet approved — drives whether "Approve all" shows at all. */
  pendingCount: number;
  onOpenApproveAll: () => void;
  onBack?: () => void;
  onContinue: () => void;
  /** True while onContinue's call is in flight (Policies is always the last step when shown). */
  finishing?: boolean;
}

/**
 * Policy-first, not location-first: one modal review for every policy update, grouped by real
 * catalog section, with a toggle between the "All updates" list and a step-by-step Walk Through —
 * both read/write the same PolicyReviewGroup[] via `handlers`, so a skip made in either shows in
 * the other immediately. Approving is never required to continue — anything not approved stays
 * pending, to come back to later.
 */
function PoliciesScreen({
  groups,
  needsReview,
  onFixNeedsReview,
  unmatchedPolicyRows,
  onGoToDetails,
  allLocationOptions,
  handlers,
  pendingCount,
  onOpenApproveAll,
  onBack,
  onContinue,
  finishing = false
}: PoliciesScreenProps): React.ReactElement {
  const [walkThroughOpen, setWalkThroughOpen] = useState(false);
  const [walkThroughStartKey, setWalkThroughStartKey] = useState<string | null>(null);

  const appliedCount = groups.filter((group) => (group.scope === "org" ? group.orgApplied : group.applied)).length;
  const locationCount = new Set(groups.flatMap((group) => group.locations.map((location) => location.locationId))).size;

  const openWalkThroughAt = (groupKey: string | null): void => {
    setWalkThroughStartKey(groupKey);
    setWalkThroughOpen(true);
  };

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-3xl font-semibold text-grey-900">Review policies</h1>

      {groups.length > 0 && (
        <div className="flex items-end justify-between gap-4">
          <div className="min-w-0 pb-0.5">
            {pendingCount > 0 && (
              <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} onClick={onOpenApproveAll}>
                Approve all
              </Button>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-[12px] font-semibold text-grey-500">View</span>
            <div className="flex overflow-hidden rounded-[4px] border border-grey-300">
              <button
                type="button"
                onClick={() => setWalkThroughOpen(false)}
                className={filterClasses(
                  "px-3 py-[5px] text-[13px] font-semibold whitespace-nowrap transition-colors",
                  !walkThroughOpen ? "bg-grey-900 text-white" : "bg-white text-grey-700 hover:bg-grey-50"
                )}
              >
                All updates
              </button>
              <button
                type="button"
                onClick={() => openWalkThroughAt(null)}
                className={filterClasses(
                  "px-3 py-[5px] text-[13px] font-semibold whitespace-nowrap transition-colors",
                  walkThroughOpen ? "bg-grey-900 text-white" : "bg-white text-grey-700 hover:bg-grey-50"
                )}
              >
                Walk through
              </button>
            </div>
          </div>
        </div>
      )}

      {unmatchedPolicyRows.length > 0 && <UnmatchedPolicyNotice rows={unmatchedPolicyRows} onGoToDetails={onGoToDetails} />}

      <NeedsReviewList groups={needsReview} busyGroupKeys={handlers.busyGroupKeys} onFix={onFixNeedsReview} />

      {groups.length === 0 && needsReview.length === 0 ? (
        <div className="rounded-[5px] border border-grey-200 bg-grey-50 px-5 py-8 text-center text-[13px] text-grey-500">
          Nothing left to review yet: assign the location{unmatchedPolicyRows.length === 1 ? "" : "s"} above to see its policy updates here.
        </div>
      ) : groups.length > 0 ? (
        <PolicyUpdatesList groups={groups} allLocationOptions={allLocationOptions} handlers={handlers} />
      ) : null}

      <FooterBar
        title={`${appliedCount} of ${groups.length} update${groups.length === 1 ? "" : "s"} approved · ${locationCount} location${locationCount === 1 ? "" : "s"} affected`}
        caption="Anything not approved stays pending, you can come back to it later."
        action={
          <div className="flex items-center gap-2">
            {onBack && (
              <Button color={ButtonColor.WHITE} onClick={onBack}>
                Back
              </Button>
            )}
            <Button color={ButtonColor.ORANGE} onClick={onContinue} loading={finishing}>
              Done
            </Button>
          </div>
        }
      />

      {walkThroughOpen && groups.length > 0 && (
        <PolicyWalkThrough
          groups={groups}
          allLocationOptions={allLocationOptions}
          startGroupKey={walkThroughStartKey}
          handlers={handlers}
          onClose={() => setWalkThroughOpen(false)}
        />
      )}
    </div>
  );
}

interface UnmatchedPolicyNoticeProps {
  rows: BulkUnmatchedRow[];
  onGoToDetails?: () => void;
}

// Blocking notice, not a dismissible tip: these rows' policy findings physically can't be diffed
// or reviewed until a location is assigned (there's no scope to diff against), so a document that
// looks fully applied can still be sitting on real, unreviewed policy content. Shown at the top of
// the Policies step whenever any unmatched row carries one — including when it's the only reason
// the step still exists at all (every matched location's own diffs already settled to zero).
function UnmatchedPolicyNotice({ rows, onGoToDetails }: UnmatchedPolicyNoticeProps): React.ReactElement {
  const names = rows.map((row) => row.identity.name?.trim() || "Unmatched location");
  return (
    <div className="flex items-start justify-between gap-4 rounded-[5px] border border-orange-200 bg-orange-50 px-5 py-4">
      <div className="flex items-start gap-3">
        <FiAlertCircle className="mt-0.5 size-5 shrink-0 text-orange-500" />
        <div>
          <p className="text-[13px] font-semibold text-grey-900">
            {rows.length} location{rows.length === 1 ? "" : "s"} still need{rows.length === 1 ? "s" : ""} to be matched before{" "}
            {rows.length === 1 ? "its" : "their"} policy updates can be reviewed
          </p>
          <p className="mt-0.5 text-[13px] text-grey-600">
            {names.join(", ")}: assign a location in Location details to unlock {rows.length === 1 ? "it" : "them"}. Nothing here applies until you
            do.
          </p>
        </div>
      </div>
      {onGoToDetails && (
        <Button color={ButtonColor.WHITE} onClick={onGoToDetails} className="shrink-0">
          Go to Location details
        </Button>
      )}
    </div>
  );
}

interface DoneScreenProps {
  /** How many fields actually got written (approved via approve-as-you-go) — never a force-write. */
  appliedFieldCount: number;
  unmatchedCount: number;
  /** True while anything in this document (data, policy, or an unassigned row) is still pending —
   * drives whether "Keep Reviewing" shows at all. */
  hasUnapproved: boolean;
  groups: ChangedGroup[];
  locations: { id: number; name: string }[];
  onDone: () => void;
  onGoToPropertyManager: () => void;
  /** Back to this same document's Location details step, exactly where it left off. */
  onKeepReviewing: () => void;
}

function DoneScreen({
  appliedFieldCount,
  unmatchedCount,
  hasUnapproved,
  groups,
  locations,
  onDone,
  onGoToPropertyManager,
  onKeepReviewing
}: DoneScreenProps): React.ReactElement {
  // "Done" is a sign-off, not a write — everything here already landed via approve-as-you-go, so
  // this screen reports what actually got approved, not "everything from the document," and treats
  // a partial pass (some fields still pending) as the normal case, not an error state.
  const title = appliedFieldCount > 0 ? `${appliedFieldCount} field${appliedFieldCount === 1 ? "" : "s"} applied` : "Nothing new to apply";
  return (
    <div className="mx-auto flex max-w-[760px] flex-col items-center gap-8 px-8 py-16 text-center">
      <div className="flex size-16 items-center justify-center rounded-full bg-teal-800 text-white">
        <FiCheck className="size-8" />
      </div>
      <div>
        <h1 className="font-poppins text-3xl font-semibold text-grey-900">{title}</h1>
        <p className="mx-auto mt-2 max-w-lg text-base text-grey-600">
          {appliedFieldCount > 0
            ? "Come back to this document anytime, anything you didn’t approve is saved for later."
            : "Come back to this document anytime, nothing here was approved yet; it’s all still saved and waiting."}
          {unmatchedCount > 0
            ? ` ${unmatchedCount} unassigned row${unmatchedCount === 1 ? "" : "s"} ${unmatchedCount === 1 ? "is" : "are"} waiting in Document Upload.`
            : ""}
        </p>
      </div>

      {groups.length > 0 && (
        <div className="w-full overflow-hidden rounded-md border border-grey-200 text-left shadow-panel">
          <div className="border-b border-grey-200 bg-grey-50 px-5 py-3.5 text-[11px] font-bold tracking-wider text-grey-500 uppercase">
            What changed
          </div>
          {groups.map((group) => (
            <div key={group.family} className="flex items-center justify-between border-b border-grey-100 px-5 py-3.5 last:border-b-0">
              <span className="text-sm text-grey-900">{group.family}</span>
              <span className="text-sm text-grey-500 tabular-nums">
                {group.fields} field{group.fields === 1 ? "" : "s"} across{" "}
                {group.locations === "all" ? "all locations" : `${group.locations} location${group.locations === 1 ? "" : "s"}`}
              </span>
            </div>
          ))}
          {locations.length > 0 && (
            <div className="flex flex-wrap gap-2 border-t border-grey-200 px-5 py-4">
              {locations.map((location) => (
                <span key={location.id} className="rounded-[4px] border border-grey-200 px-2.5 py-1 text-[13px] font-semibold text-grey-700">
                  {location.name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-center gap-2">
        {hasUnapproved && (
          <Button color={ButtonColor.WHITE} onClick={onKeepReviewing}>
            Keep Reviewing
          </Button>
        )}
        <Button color={ButtonColor.WHITE} onClick={onGoToPropertyManager}>
          Go to Property Manager
        </Button>
        <Button color={ButtonColor.ORANGE} onClick={onDone}>
          Upload another document
        </Button>
      </div>
    </div>
  );
}

interface FooterBarProps {
  title: string;
  caption: string;
  action: React.ReactNode;
}

function FooterBar({ title, caption, action }: FooterBarProps): React.ReactElement {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md border border-grey-200 bg-grey-50 px-6 py-5">
      <div>
        <div className="text-[15px] font-semibold text-grey-900">{title}</div>
        <div className="text-[13px] text-grey-500">{caption}</div>
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  );
}
